// ============================================================
// constants/VEHICLE_PRESETS.js
// EV Powertrain Simulator — 차량 프리셋 상수
// Extracted from ev-sim_v1_0_Verification_45.jsx (Phase 1)
// ============================================================

// ── DEFAULT_PARAMS ──
const DEFAULT_PARAMS = {
  // Vehicle
  m: 1850, r_wheel: 0.33, CdA: 0.49, Crr: 0.0095, rho: 1.2, mu: 1.0,
  // Powertrain
  gear_ratio: 9.5, eta_driveline: 0.95, T_max_motor: 400, P_max_wheel: 220000,
  // Battery
  P_max_battery: 250000, P_max_regen: 70000, battery_energy: 75, soc_init: 0.9,
  pack_mass_kg: 400,     // [A31-Fix-3] 배터리팩 질량 [kg] — 에너지밀도(Wh/kg) 정확 계산용
  eta_motor_inv: 0.91, aux_power: 1500,
  // Motor electric params (IPMSM)
  V_dc: 400, I_max_rms: 600,
  m_a: 1.0,
  // Environment
  grade_deg: 0, road_condition: "dry",
  // Battery detailed
  R_batt: 0.08, Q_nom: 200, batt_temp: 25,
  // Inverter detailed - CORRECTED for realistic SiC MOSFET
  V_f_diode: 0.5,            // [v6-fix8] SiC 순방향 전압
  R_sw: 0.0008,              // [v6-fix8] SiC 전도저항
  E_on_off: 0.003,           // 스위칭 에너지 [J/pulse]
  f_sw: 10000,               // 스위칭 주파수 [Hz]
  inv_aux_power: 50,         // 게이트드라이버 등 보조 [W]
  // Motor loss model
  k_h: 0.02, k_e: 0.0001, k_m0: 20, k_m1: 0.01, k_m2: 0.000005,
  // Gear loss model
  k_g0: 30, k_g1: 0.002, k_g2: 0.00001,
  // ══════════ NEW: Thermal Parameters ══════════
  T_amb: 25,             // Ambient temperature (°C)
  // Battery thermal
  C_th_batt: 80000,      // Battery thermal capacity [J/°C]
  R_th_bc: 0.8,          // Battery→coolant thermal resistance [°C/W]
  R_th_ba: 5.0,          // Battery→ambient thermal resistance [°C/W]
  k_R_batt: 0.6,         // Battery Ri cold-increase coeff — Arrhenius 지수 진폭 (τ=25°C 고정). 0.6 → −20°C 4× / 0°C 2× (LiNMC 실측 범위)
  // Inverter thermal
  C_th_inv: 2000,         // [v8-fix9] Inverter thermal capacity [J/°C] — SiC 모듈+히트싱크 (기존 500은 비현실적)
  R_th_ic: 0.08,          // [v8-fix9] Inverter→coolant [°C/W] — 수냉 히트싱크 (기존 0.5는 공냉 수준)
  k_inv_temp: 0.003,     // Inverter loss temp coeff [/°C]
  // Motor thermal
  C_th_motor: 12000,      // [v8-fix9] Motor thermal capacity [J/°C] — 고정자+하우징 (기존 8000 → 12000)
  R_th_mc: 0.15,          // [v8-fix9] Motor→coolant [°C/W] — 수냉 자켓 (기존 0.3 → 0.15)
  alpha_Cu: 0.0039,      // Copper temp coeff [/°C]
  k_lambda: 0.001,       // PM flux demagnetization coeff [/°C]
  T_ref: 25,             // Reference temperature (°C)
  // Coolant / Radiator
  C_th_cool: 15000,      // Coolant thermal capacity [J/°C]
  UA_rad: 200,           // Radiator heat transfer [W/°C]
  phi_speed: 0.5,        // Airflow speed enhancement factor
  P_pump: 150, P_fan: 100,
  // Derate thresholds
  T_batt_derate_start: 45, T_batt_derate_end: 55,
  T_batt_cold_limit: 5,    // Below this, cold derate applies
  T_inv_derate_start: 95, T_inv_derate_end: 110,
  T_motor_derate_start: 140, T_motor_derate_end: 160,
  // Power limits (base)
  // Regen detailed
  regen_blend_low_speed: 8, // Below this ω [rad/s], regen fades
  eta_regen_motor: 0.92,    // Motor efficiency in generation mode
  // Dynamic Crr
  Crr_v_coeff: 0.0002,     // Crr increase with v² [s²/m²]
  Crr_temp_coeff: 0.01,    // Crr increase per °C below 20°C
  // Altitude
  altitude: 0,              // Altitude [m] for rho correction
  // ══════════ Inverter Controller (FOC) ══════════
  // [v9-fix1] IMC 기반 PI 게인 설계 (BW_current=800Hz, BW_speed=10Hz)
  // 전류 제어기: Kp=L×α, Ki=R×α (α=2π×800)
  // d축 Ld=0.3mH, q축 Lq=0.6mH, Rs=15mΩ
  Kp_id: 1.51, Ki_id: 75, Kp_iq: 3.02, Ki_iq: 75,
  // 속도 제어기: Kp=J_eq×α_s, Ki=Kp×α_s/3 (J_eq≈2.28, α_s=2π×10)
  Kp_spd: 143.4, Ki_spd: 3003,
  // [R-12-05] 위치 루프 독립 PID 파라미터
  Kp_pos: 5.0, Ki_pos: 0.5, Kd_pos: 0.05,
  mtpa_enable: true, Id_ref_base: 0,
  dead_time: 2.5e-6, ctrl_dt: 0.0001, carrier_freq: 10000,
  // [v9-fix5] 전류 PI 실제 연동 옵션 (true: PI제어, false: 1차지연근사)
  foc_current_pi_enable: false,
  // [R-12-01] 가변 CdA 그릴 셔터 — DEFAULT_PARAMS 명시
  // [R-12-02] Launch Control
  launch_control_enable: false,
  lc_slip_target: 0.12,          // 목표 슬립률 (최적 μ 영역)
  lc_torque_ramp_rate: 800,       // [Nm/s] 토크 램프 속도
  lc_slip_kp: 3000,               // 슬립 PI Kp
  lc_slip_ki: 150,                // 슬립 PI Ki
  lc_staging_rpm: 3000,           // 발차 대기 RPM
  // [R-12-04] 회생 에너지 최적화
  regen_optimizer_enable: false,
  regen_opt_weight_energy: 0.7,   // 에너지 회수 가중치
  regen_opt_weight_comfort: 0.3,  // 승차감(저크) 가중치
  // [R-12-06] MRAC
  mrac_gamma_p: 0.5, mrac_gamma_r: 0.5,  // 적응 게인 학습률
  // [R-12-07] H∞
  hinf_gamma: 2.0,               // H∞ 성능 목표 (γ)
  hinf_ms:    2.0,               // 최대 허용 감도 피크 Ms (W1 DC 게인 = Ms)
  hinf_w1_bw: 10, hinf_w2_bw: 1000,  // W1/W2 대역폭 [Hz]
  // [R-12-08] MPC
  mpc_N: 10,                     // 예측 지평
  mpc_q_pos: 100, mpc_r_u: 1,   // 상태/입력 가중행렬
  mpc_dU_max: 50,                // 입력 변화량 제한 [Nm]
  // ══════════ [R-13-NEW] 4WS 독립 서스펜션 · 능동 서스펜션 · 조향 피드백 · 전복 ══════════
  active_suspension_enable: false,  // 능동 서스펜션 활성화
  act_Kp: 15000,                    // 능동 서스펜션 비례 게인 [N/m]
  act_Ki: 800,                      // 능동 서스펜션 적분 게인
  act_F_max: 8000,                  // 액추에이터 최대력 [N]
  act_roll_gain: 3000,              // 롤 억제 피드포워드 게인
  front_weight_bias: 0.5,           // 전방 무게배분 (0.5 = 50:50)
  // 조향 피드백 (EPS)
  trail_pneumatic: 0.04,            // 공압 트레일 [m]
  trail_caster: 0.02,               // 캐스터 트레일 [m]
  steering_ratio: 16,               // 스티어링 기어비
  J_steering_wheel: 0.04,           // SW 관성 모멘트 [kg·m²]
  b_steering_damp: 0.5,             // SW 감쇠 [N·m·s/rad]
  eps_speed_gain: 0.8,              // EPS 속도감응 게인
  // ══════════ BMS ══════════
  bms_v_cell_max: 4.20, bms_v_cell_min: 2.80,
  bms_i_discharge_max: 500, // [A10] 기본 방전 전류 한계 [A] (500A × 400V = 200kW)
  bms_i_charge_max: 200,    // [A10] 기본 충전 전류 한계 [A]
  bms_soc_charge_cutoff: 0.98, bms_soc_discharge_cutoff: 0.05,
  bms_t_charge_min: 0, bms_t_charge_max: 45,
  bms_t_discharge_min: -20, bms_t_discharge_max: 60,
  bms_cell_count_s: 96, bms_cell_count_p: 4,
  bms_regen_soc_taper_start: 0.85,
  bms_cold_derate_temp: 5, bms_cold_derate_factor: 0.5,
  // ══════════ Correlation K-Factors (physics tuning) ══════════
  K_aero: 1.0, K_roll: 1.0, K_grade: 1.0,
  K_cu: 1.0, K_fe: 1.0, K_mech: 1.0,
  K_inv_cond: 1.0, K_inv_sw: 1.0,
  K_batt_r: 1.0, K_gear: 1.0, K_regen: 1.0,
  // Brake & Sensor K-Factors
  K_brake_mu: 1.0,       // 브레이크 마찰계수 보정
  K_brake_thermal: 1.0,  // 브레이크 열 전달 보정
  K_abs: 1.0,            // ABS 슬립 타겟 보정
  K_sensor_noise: 1.0,   // 센서 노이즈 강도 보정
  K_kalman_Q: 1.0,       // 칼만 필터 프로세스 노이즈 보정
  K_kalman_R: 1.0,       // 칼만 필터 측정 노이즈 보정
  // ══════════ PRIORITY 1: 2RC Battery Model ══════════
  battery_model: '2RC',
  // ══════════ PRIORITY 1: MTPV ══════════
  // ══════════ PRIORITY 1: Pacejka Tire ══════════
  tire_pacejka_enable: true,
  tire_pC1: 1.65, tire_pD1: 1.05, tire_pD2: -0.12,
  tire_pK1: 28.0, tire_pK2: 0.5, tire_pK3: 0.35,
  tire_pE1: 0.1, tire_pE2: -0.05,
  tire_Fz_nominal: 4500, tire_temp_init: 25, tire_temp_optimal: 80,
  tire_temp_nominal: 80,           // [Step2] lateralDynamicsStep Combined Slip 기준 타이어 온도 [°C]
  // ══════════ PRIORITY 1: AWD ══════════
  awd_enable: false, torque_split_front: 0.4,
  // ══════════ PRIORITY 2: 센서리스 제어 ══════════
  sensorless_enable: false,  // Set true to enable
  Kp_pll: 100, Ki_pll: 5000,  // PLL gains for back-EMF observer
  // ══════════ PRIORITY 2: 동적 토크 분배 ══════════
  dynamic_distribution_enable: false,  // Requires awd_enable=true
  efficiency_bias_enable: false,       // Optimize for efficiency
  // ══════════ PRIORITY 2: SOH 모델 ══════════
  soh_enable: false,  // State of Health tracking
  B_cycle: 7.543e-3, Ea_cycle: 31700, z_DOD: 0.55,
  k_cal: 4.14e-10, Ea_cal: 24500, alpha_SOC: 0.75,
  // ══════════ PRIORITY 2: 데드타임 보상 ══════════
  // ══════════ PRIORITY 3: 토크 벡터링 ══════════
  torque_vectoring_enable: false,  // Left/Right independent torque control
  Kp_yaw: 1000, Ki_yaw: 500,       // Yaw rate PI gains [Nm/(rad/s)]
  tv_slip_limit: 0.2,              // Slip limit for TV
  // ====== v4.0: [TV] AWD/Torque Vectoring Enhancement Parameters ======
  // [TV-1] Ku auto-calculation
  ku_auto_calc: true,              // Ku=m/(C_af*C_ar)*(a*C_af-b*C_ar) auto
  K_understeer: 0.001,             // Manual understeer coeff [rad*s2/m2]
  // [TV-2] Yaw rate saturation
  psi_dot_sat_enable: true,        // psi_dot_max = mu*g/v physical limit
  psi_dot_margin: 0.90,            // saturation margin (0~1)
  // [TV-3] Adaptive front/rear distribution
  lambda_fr_adaptive_enable: true, // adaptive lambda_fr
  lambda_fr_base: 0.4,             // base front ratio lambda0
  lambda_fr_min: 0.0,              // min front ratio
  lambda_fr_max: 0.6,              // max front ratio
  k_lambda_mu: 0.3,                // friction deviation gain k_mu
  k_lambda_yaw: 2.0,               // yaw error correction gain k_US [1/(rad/s)]
  mu_ref_nominal: 1.0,             // reference friction mu_ref
  // [TV-4] Roll stiffness distribution
  roll_stiff_front: 22000,         // front roll stiffness kr_f [Nm/rad]
  roll_stiff_rear:  18000,         // rear roll stiffness kr_r [Nm/rad]
  // [TV-5] TV front/rear weight
  tv_weight_front: 0.3,            // wf: front TV contribution (0~1)
  tv_weight_rear:  0.7,            // wr: rear TV contribution (wf+wr=1)
  // [TV-6] Friction circle scaling
  friction_ellipse_tv_enable: true,// friction circle constraint after TV
  mu_estimate_enable: false,       // mu_hat real-time estimation (slip-based)
  // ════════════ v2.0: ① WLTP 고도화 ══════════
  wltp_ef_grid: 459,               // 전력 배출계수 [gCO₂/kWh] (한국 2024 KEA 기준)
  wltp_soc_usable_max: 0.90,       // WLTP 사용 가능 SOC 상한
  wltp_soc_usable_min: 0.10,       // WLTP 사용 가능 SOC 하한
  wltp_tracking_kp: 3.0,           // 속도 추종 PI — Kp [1/s]
  wltp_tracking_ki: 0.1,           // 속도 추종 PI — Ki [1/s²]
  // ── [A20] 드라이빙 모드별 속도 추종 PI 게인 스케줄링 ──
  // 기본값(wltp_tracking_kp/ki) 대비 모드별 배율. 0이면 기본값 사용.
  // ECO: 부드러운 추종 / NORMAL: 표준 / SPORT: 빠른 응답 / SNOW: 완만 / OFF_ROAD: 중간
  gs_kp_eco:      1.5,   gs_ki_eco:      0.05,   // ECO — 완만한 추종
  gs_kp_normal:   3.0,   gs_ki_normal:   0.10,   // NORMAL — 표준
  gs_kp_sport:    5.5,   gs_ki_sport:    0.20,   // SPORT — 빠른 응답
  gs_kp_snow:     2.0,   gs_ki_snow:     0.06,   // SNOW — 저슬립 제어
  gs_kp_offroad:  2.5,   gs_ki_offroad:  0.08,   // OFF_ROAD — 중간
  wltp_lookahead_s: 1.0,           // [A17] WLTP/FTP look-ahead feedforward [s] — 전방 속도 예견 보상
  wltp_regen_aggressiveness: 0.8,  // 회생 제동 적극성 [0~1]
  // ── [A17] 운전자 입력 / 신호 처리 파라미터 ──────────────────────────
  app_deadzone_pct: 2,             // APP 데드존 (%) — 페달 무반응 영역 (기계 유격·노이즈 보정)
  app_roc_limit: 100,              // APP 최대 변화율 [%/s] — 급격한 페달 조작 완화
  bpp_deadzone_pct: 3,             // BPP 데드존 (%) — 브레이크 무반응 영역
  brake_detect_threshold: 5,       // 회생/유압 전환 시작 BPP 임계값 (%)
  // ════════════ [R-10-01] GAP-06: PID 드라이버 모델 ══════════
  driver_pid_enable: true,          // PID 드라이버 모델 활성화 (true=PID, false=throttle 고정)
  // ════════════ [Step5] MPC 드라이버 모델 ══════════
  driver_mpc_enable: false,         // MPC 드라이버 활성화 (driver_pid_enable보다 우선 적용)
  // ════════════ [CascadePID] 속도 루프 명시적 PI 적분기 ══════════
  // 우선순위: cascade_speed_enable > driver_mpc_enable > driver_pid_enable > throttle 고정
  // Zone 2 파라미터(Kp_spd/Ki_spd)를 실제 속도 PI 루프로 연동.
  // DriveMode 배율(_applyDriveModeGainsInline)은 cascade에도 자동 적용됨.
  cascade_speed_enable: false,      // Cascade 속도 PI 루프 활성화
  cascade_spd_i_limit: 400,         // 속도 PI 적분 anti-windup 한계 [Nm]
  Kp_drv: 0.8,                     // 드라이버 PID — 비례 게인
  Ki_drv: 0.15,                    // 드라이버 PID — 적분 게인
  Kd_drv: 0.05,                    // 드라이버 PID — 미분 게인
  drv_max_jerk: 8.0,               // 드라이버 최대 Jerk 허용 [m/s³] (페달 변화율 제한)
  drv_pedal_lag: 0.12,             // 드라이버 반응 지연 시간상수 [s]
  drv_int_limit: 1.5,              // 적분 항 anti-windup 한계
  // ════════════ [R-10-02] GAP-10: 다단 기어 변속 모델 ══════════
  multi_gear_enable: false,         // 다단 변속기 활성화 (false=단일 gear_ratio 유지)
  gear_ratios: [11.5, 6.0],        // [1단 감속비, 2단 감속비] — 포르쉐 타이칸 2-speed 기준
  gear_shift_speed_kmh: 65,        // 1→2단 업시프트 기준 속도 [km/h]
  gear_shift_down_speed_kmh: 50,   // 2→1단 다운시프트 기준 속도 [km/h]
  gear_shift_torque_dip: 0.12,     // 변속 중 토크 딥 비율 [0~1] (충격 모델)
  gear_shift_duration_s: 0.25,     // 변속 소요 시간 [s]
  gear_shift_soc_boost: 0.7,       // SOC < 이 값에서 2단 우선 사용 (효율 최적화)
  // ════════════ v2.0: ② 2-DOF Bicycle 횡/요 다이나믹스 ══════════
  Iz: 2500,                         // 요 관성모멘트 [kg·m²]
  Ixx: 800,                         // [Step3] 롤 관성모멘트 [kg·m²] (Iz의 약 1/3 수준)
  Cf: 80000,                        // 전축 코너링 강성 [N/rad]
  Cr: 90000,                        // 후축 코너링 강성 [N/rad]
  h_cg: 0.54,                       // 무게중심 높이 [m]
  wheel_base: 2.875,                // 축간거리 [m]
  delta_steer_max: 0.524,           // 최대 조향각 [rad] (~30°)
  // ════════════ [Step3] 7DOF 확장 ══════════
  seven_dof_enable: false,          // 7DOF 모드 (roll φ + heave z_cg DOF 추가) — lateral_enable과 독립
  roll_damp: 3000,                  // 롤 감쇠 계수 [N·m·s/rad] (roll_stiff_front/rear는 L183 기존 값 공유)
  // ════════════ v2.0: ③ 열 펌프 COP 고도화 ══════════
  heat_pump_enable: true,
  hp_refrigerant: 'R744',           // 냉매 ('R744'=CO₂, 'R134a', 'R1234yf')
  eta_hp_heat: 0.47,                // 가열 모드 압축기 효율 [−]
  eta_hp_cool: 0.52,                // 냉각 모드 압축기 효율 [−]
  cop_hp_heat_ref: 2.5,             // 가열 COP 상한 (슬라이더용)
  cop_hp_cool_ref: 3.0,             // 냉각 COP 상한 (슬라이더용)
  T_hp_target: 22,                  // 실내 목표 온도 (°C)
  P_hvac_max: 5000,                 // HVAC 최대 전력 [W]
  // ════════════ v2.0: ④ OCV-SOC Nernst + LUT 이중 모델 ══════════
  ocv_lut_enable: true,
  ocv_lut_weight: 0.70,             // LUT 가중치 (Nernst 가중치 = 1 − this)
  // ════════════ v2.0: ⑤ Quarter-Car 서스펜션 ══════════
  suspension_enable: false,
  k_spring: 25000,                  // 스프링 강성 [N/m]
  c_damper: 2000,                   // 감쇠 계수 [N·s/m]
  m_unsprung: 40,                   // 언스프렁 질량 (1 코너) [kg]
  k_tire_vertical: 200000,          // 타이어 수직 강성 [N/m]
  // ════════════ v2.0: ⑥ BMS 셀 밸런싱 ══════════
  bms_R_bal: 10,                    // 패시브 밸런싱 저항 [Ω]
  bms_I_bal_active: 5.0,            // 액티브 밸런싱 전류 [A]

  // ════════════ v3.0: [A] 측풍 (Crosswind) ════════════
  crosswind_enable: true,          // 측풍 공기저항 활성화
  v_wind: 0,                       // 풍속 [m/s] (양수=정면풍 유사)
  wind_angle_deg: 90,              // 측풍 각도 β (°) (0=정면, 90=측면)
  // [Advance34-P7] 중복 정의 통일:
  //   wind_speed_ms: rlResist/simLaunch 등 핵심 연산에 사용하는 맞바람 속도
  //   wind_speed/wind_direction: 레거시 (enhanced 섹션) — wind_speed_ms 우선
  wind_speed_ms: 0,                // 맞바람 속도 [m/s] — 헤더 rlHeadwind와 직결 (A33-R2)
  // ════════════ v3.0: [B] 자기 포화 & Steinmetz ════════════
  mag_sat_enable: true,            // 자기 포화 전압방정식 활성화
  Ld_sat_k1: 0.12,                // L_d 포화계수 k1 (i_d 의존)
  Ld_sat_k2: 0.05,                // L_d 포화계수 k2 (i_q 의존)
  Lq_sat_k1: 0.08,                // L_q 포화계수 k1
  Lq_sat_k2: 0.04,                // L_q 포화계수 k2
  steinmetz_kh: 100.0,            // Steinmetz 히스테리시스 계수 k_h [W/(m³·Hz·T^n)] — M270-50A 규소강판 기준 (수정 전: 0.0025, 단위계 불일치로 30,000배 과소)
  steinmetz_kc: 1.0,              // Steinmetz 와전류 계수 k_e [W/(m³·Hz²·T²)]       — M270-50A 규소강판 기준 (수정 전: 1.5e-4, 단위계 불일치로 4,500배 과소)
  steinmetz_n: 1.8,               // Steinmetz 지수 n (B^n)
  B_peak_ref: 1.2,                // 참조 자속밀도 B_peak [T]
  V_iron: 0.003,                  // 고정자 철심 체적 V_iron [m³] — 150 kW급 PMSM 기준 (kh·ke 와 함께 P_fe 크기 결정)
  // ════════════ v3.0: [C] Li-Plating 감지 ════════════
  li_plating_enable: true,        // 리튬 플레이팅 감지 활성화
  V_anode_threshold: 0.0,         // 플레이팅 발생 기준 [V]
  // ════════════ v3.0: [D/E] Combined Slip 완전체 ════════════
  friction_ellipse_enable: true,
  tire_mu_x_ratio: 1.0,            // 종방향 마찰계수 비율 (기준 μ 대비)
  tire_mu_y_ratio: 0.9,            // 횡방향 마찰계수 비율 (전형적: 종방향의 90%)  // 마찰 타원 모델 활성화
  // F_y Pacejka E factor
  // ════════════ v3.0: [F] ESC/TCS ════════════
  tcs_enable: true,               // TCS 활성화
  tcs_slip_threshold: 0.18,       // TCS 개입 슬립비 임계값
  tcs_torque_reduction: 0.7,      // TCS 토크 저감 계수
  // ════════════ v3.0: [G] 가변 CdA / 그릴 셔터 ════════════
  grille_shutter_enable: true,    // 그릴 셔터 모델 활성화
  CdA_closed: 0.46,               // 셔터 닫힘 시 CdA [m²] (고속/저온)
  CdA_open: 0.52,                 // 셔터 열림 시 CdA [m²] (저속/고온)
  grille_open_speed_kph: 80,      // 셔터 열리는 기준 차속 [km/h]
  grille_open_T_motor: 100,       // 셔터 열리는 기준 모터 온도 (°C)
  CdA_speed_coeff: 0.0003,        // 속도 의존 CdA 증가 계수 [m²/(m/s)²]
  // ✅ [FIX] 공기역학 다운포스 ↔ Fz 실시간 루프
  // ClA: 양력면적 [m²]. 음수 = 다운포스(접지력 증가), 양수 = 양력. 기본 0 = 중립
  // 일반 EV: 0 ~ -0.05, 스포츠 세단: -0.04 ~ -0.10, 고성능 스포츠카: -0.20 ~ -0.80
  ClA: 0,                         // 양력면적 [m²] (0 = 기존 동작 완전 보존)
  aero_balance_f: 0.35,           // 전륜 다운포스 배분 비율 (기본 35% 전 / 65% 후)
  // ══════════ PRIORITY 3: HF Injection ══════════
  hf_injection_enable: false,      // High-frequency injection sensorless
  f_hf_injection: 800,             // Injection frequency [Hz]
  V_hf_injection: 15,              // Injection voltage [V]
  hf_transition_low: 50,           // Low transition speed [rad/s]
  hf_transition_high: 100,         // High transition speed [rad/s]
  // ══════════ PRIORITY 3: 3D 열 분포 ══════════
  thermal_3d_enable: false,        // 3D cell-by-cell thermal tracking
  thermal_3d_nx: 12,               // Cells in X direction
  thermal_3d_ny: 8,                // Cells in Y direction
  thermal_3d_nz: 4,                // Cells in Z direction
  R_th_cell_cell: 0.5,             // Cell-to-cell thermal resistance [°C/W]
  R_th_cell_coolant: 0.3,          // Cell-to-coolant [°C/W]
  R_th_cell_ambient: 10,           // Cell-to-ambient [°C/W]
  C_th_cell: 1000,                 // Cell thermal capacity [J/°C]
  // ══════════ PRIORITY 3: 고조파 억제 ══════════
  harmonic_rejection_enable: false, // Harmonic rejection control
  rc_ripple_enable: false,          // [R-05] 반복제어(RC) 토크리플 보상 — simLaunch/simReaccel FOC 루프 연동
  harmonics_to_compensate: [5, 7, 11, 13], // Harmonic orders
  harmonic_amp_5: 0.02, harmonic_phase_5: 0,  // 5th harmonic
  harmonic_amp_7: 0.015, harmonic_phase_7: 0, // 7th harmonic
  harmonic_amp_11: 0.01, harmonic_phase_11: 0, // 11th harmonic
  harmonic_amp_13: 0.01, harmonic_phase_13: 0, // 13th harmonic
  rc_Q: 0.95, rc_Kr: 5.0,          // Repetitive controller params
  // ════════════ PHASE 4: ABS & 브레이크 열 ════════════
  abs_enable: true,
  abs_lambda_target: 0.10,
  abs_Kp: 2000, abs_Ki: 100,
  m_brake_disc: 8.0,
  c_brake_disc: 460,
  A_brake_disc: 0.04,
  h_brake_conv: 80,
  T_brake_init: 25,
  mu_brake_nominal: 0.38,
  T_brake_fade_start: 300,
  T_brake_fade_end: 600,
  mu_brake_faded: 0.20,
  // ════════════ PHASE 4: 센서 노이즈 & 칼만 필터 ════════════
  sensor_noise_enable: false,
  sigma_v: 0.05,
  sigma_a: 0.1,
  sigma_soc: 0.005,
  sigma_T: 0.5,
  tau_sensor: 0.02,
  kalman_enable: true,   // [A10] 기본 활성 — SOC 추정 정확도 향상
  Q_kf: 1e-5,
  R_kf: 1e-3,
  // ════════════ [Step4] β Sideslip EKF ════════════
  beta_ekf_enable: false,          // β 사이드슬립각 EKF 관측기 활성화 (lateral_enable 필요)
  Q_beta_ekf: 1e-4,               // 프로세스 잡음: β [rad²]
  Q_psi_ekf: 1e-3,                // 프로세스 잡음: ψ̇ [rad²/s²]
  R_ay_ekf: 0.5,                  // 측정 잡음: 횡가속도계 ay [m²/s⁴]
  R_psi_ekf: 0.02,                // 측정 잡음: 요레이트 센서 ψ̇ [rad²/s²]
  beta_ekf_blend: 0.7,            // EKF 융합비 (1=완전 EKF, 0=물리 모델 직접적분)
  // ══════════ NEW v3: Drive Mode ══════════
  drive_mode: 'NORMAL',            // 'ECO' | 'NORMAL' | 'SPORT'
  motor_config: 'dual',            // [A35] 'dual' | 'single_rear' | 'single_front'
  motor_front_power_ratio: 0.24,  // [A35] 전륜 모터 출력 비율 (듀얼 모터 시)
  rear_weight_ratio: 0.55,        // [A35-BUG-M4] 후륜 정적 하중 비율 (싱글 모터 접지한계 계산용, 0.5~0.65)
  one_pedal_enable: false,         // 원페달 드라이빙 활성화
  // ══════════ NEW v3: Drive Mode Torque Maps ══════════
  // ECO mode: pedal sensitivity (응답감도) + max torque scale
  eco_pedal_sensitivity: 0.6,      // 0~1 (낮을수록 완만한 응답)
  eco_max_torque_ratio: 0.7,       // 최대 토크 비율 (0.5~1.0)
  eco_regen_strength: 0.5,         // 회생 강도 (0~1)
  // NORMAL mode
  normal_pedal_sensitivity: 0.85,
  normal_max_torque_ratio: 1.0,
  normal_regen_strength: 0.7,

  // ══════════ CALIBRATION v1: Traction Torque Map LUT ══════════
  // traction_lut_enable: LUT 활성화 시 Power-function 대신 2D 보간 사용
  traction_lut_enable: true,  // [A06-UI2] 기본값 true (LUT ON)
  // V_POINTS: 차속 [km/h] 8포인트 (열)
  // APP_POINTS: 페달 개도 (%) 6포인트 (행)
  // 값: 목표 토크 (N·m) (T_max_motor=400N·m 기준)
  // 형식: [app0%][app10%][app25%][app50%][app75%][app100%] × [0,20,40,60,80,100,120,140 km/h]
  traction_lut_eco:    [[0,0,0,0,0,0,0,0],[28,28,24,24,20,20,16,16],[72,68,64,60,56,52,48,44],[140,136,128,120,112,104,96,88],[208,200,192,180,168,156,144,132],[280,272,260,248,232,216,200,184]],
  traction_lut_normal: [[0,0,0,0,0,0,0,0],[40,40,36,36,32,32,28,28],[100,96,92,88,84,76,72,68],[200,196,188,180,172,160,152,140],[300,292,280,268,252,236,220,204],[400,392,380,364,348,328,308,288]],
  traction_lut_sport:  [[0,0,0,0,0,0,0,0],[48,48,44,44,40,40,36,36],[120,116,112,108,104,96,92,88],[240,236,228,220,212,200,188,176],[360,352,340,328,312,292,272,252],[460,452,440,424,408,388,368,348]],
  traction_lut_snow:   [[0,0,0,0,0,0,0,0],[16,16,16,12,12,12,12,12],[44,40,40,36,36,32,32,28],[88,84,80,76,72,68,64,60],[132,128,124,116,108,100,96,88],[220,212,204,192,180,168,156,144]],
  traction_lut_offroad: [[0,0,0,0,0,0,0,0],[52,52,48,48,44,44,40,40],[124,120,116,112,108,100,96,92],[252,244,236,228,220,208,196,184],[376,368,356,344,328,308,288,268],[400,392,380,364,348,320,300,280]],

  // ══════════ CALIBRATION v1: Regen Torque Maps ══════════
  regen_calib_enable: false,
  // Coasting Regen: 1D LUT — 차속 [km/h] → 회생 강도 [0~1]
  regen_coast_v_pts:  [0, 5, 15, 30, 60, 90, 120, 150],
  regen_coast_str:    [0, 0, 0.15, 0.40, 0.65, 0.75, 0.80, 0.80],
  // Braking Regen: 2D LUT — BPP(%) × 차속[km/h] → 회생비율 [0~1]
  regen_brake_v_pts:   [0, 30, 60, 100, 130],
  regen_brake_bpp_pts: [0, 10, 25, 50, 75, 100],
  regen_brake_lut:     [[0,0,0,0,0],[0.3,0.35,0.4,0.35,0.25],[0.55,0.60,0.65,0.55,0.40],[0.70,0.75,0.70,0.60,0.45],[0.75,0.78,0.72,0.62,0.48],[0.78,0.80,0.73,0.63,0.50]],
  // One-Pedal Regen: 1D LUT — APP(%) 0~8% → 회생 레벨
  regen_onepedal_app_pts: [0, 1, 2, 3, 4, 5, 6, 7, 8],
  regen_onepedal_str:     [1.0, 0.90, 0.75, 0.55, 0.35, 0.15, 0.05, 0.0, 0.0],

  // ══════════ CALIBRATION v1: AWD Torque Distribution ══════════
  awd_calib_enable: false,
  // 2D LUT 공통 축
  awd_v_pts:    [0, 20, 40, 60, 80, 100, 120, 140],  // 차속 [km/h]
  awd_torq_pts: [0, 20, 40, 60, 80, 100],             // 요구토크 (%)

  // ── [Advance03] 모드별 독립 AWD 전륜 비율 LUT ─────────────────────────
  // 값: front_ratio [0.0=RWD(후100%) ~ 0.5=50:50 ~ 1.0=FWD(전100%)]
  // 행(6) = 요구토크% [0,20,40,60,80,100]
  // 열(8) = 차속[km/h] [0,20,40,60,80,100,120,140]

  // ECO: 0:100~20:80 — 효율 우선, F=μN 후륜 집중, 앞모터 최소
  awd_front_lut_eco: [
    [0.00,0.00,0.00,0.00,0.00,0.00,0.00,0.00],
    [0.10,0.08,0.05,0.05,0.05,0.05,0.05,0.05],
    [0.15,0.12,0.10,0.08,0.07,0.06,0.05,0.05],
    [0.20,0.18,0.15,0.12,0.10,0.08,0.07,0.06],
    [0.20,0.20,0.18,0.15,0.12,0.10,0.08,0.07],
    [0.20,0.20,0.20,0.18,0.15,0.12,0.10,0.08]],

  // NORMAL: 40:60~50:50 — yaw rate 균형, 일상 안정성
  awd_front_lut_normal: [
    [0.50,0.45,0.42,0.40,0.40,0.40,0.40,0.40],
    [0.50,0.45,0.42,0.40,0.40,0.40,0.40,0.40],
    [0.50,0.46,0.43,0.42,0.41,0.40,0.40,0.40],
    [0.50,0.47,0.44,0.43,0.42,0.41,0.40,0.40],
    [0.50,0.48,0.46,0.44,0.43,0.42,0.41,0.40],
    [0.50,0.50,0.49,0.48,0.47,0.45,0.43,0.42]],

  // SPORT: 20:80~30:70 — 후륜 바이어스, 코너링 M=F×r 극대화
  awd_front_lut_sport: [
    [0.30,0.28,0.25,0.22,0.20,0.20,0.20,0.20],
    [0.28,0.25,0.23,0.21,0.20,0.20,0.20,0.20],
    [0.26,0.24,0.22,0.21,0.20,0.20,0.20,0.20],
    [0.25,0.23,0.22,0.21,0.20,0.20,0.20,0.20],
    [0.24,0.23,0.22,0.21,0.20,0.20,0.20,0.20],
    [0.22,0.22,0.21,0.21,0.20,0.20,0.20,0.20]],

  // SNOW: 55:45~60:40 — 앞륜 중심, 직진성, μ 낮은 노면 스핀 방지
  awd_front_lut_snow: [
    [0.55,0.55,0.55,0.55,0.55,0.55,0.55,0.55],
    [0.58,0.57,0.56,0.55,0.55,0.55,0.55,0.55],
    [0.60,0.59,0.58,0.57,0.56,0.55,0.55,0.55],
    [0.60,0.60,0.59,0.58,0.57,0.56,0.55,0.55],
    [0.60,0.60,0.60,0.59,0.58,0.57,0.56,0.55],
    [0.60,0.60,0.60,0.60,0.59,0.58,0.57,0.56]],

  // OFF_ROAD: 50:50 + 저속 동적 벡터링 — 크롤링, 크로스 액슬 가상 락커
  awd_front_lut_offroad: [
    [0.50,0.50,0.50,0.50,0.50,0.50,0.50,0.50],
    [0.50,0.50,0.50,0.48,0.47,0.46,0.45,0.45],
    [0.50,0.50,0.48,0.46,0.45,0.44,0.43,0.42],
    [0.50,0.50,0.48,0.46,0.44,0.43,0.42,0.41],
    [0.50,0.50,0.48,0.46,0.44,0.43,0.42,0.40],
    [0.50,0.50,0.48,0.46,0.44,0.43,0.42,0.40]],

  // 모드별 LUT 활성화 (true=위 모드별 LUT 우선 / false=awd_front_lut 단일 공유)
  awd_mode_lut_enable: true,

  // 단일 공유 LUT (awd_mode_lut_enable=false 시 사용)
  awd_front_lut: [
    [0.50,0.45,0.42,0.40,0.40,0.40,0.40,0.40],
    [0.50,0.45,0.42,0.40,0.40,0.40,0.40,0.40],
    [0.50,0.46,0.43,0.42,0.41,0.40,0.40,0.40],
    [0.50,0.47,0.44,0.43,0.42,0.41,0.40,0.40],
    [0.50,0.48,0.46,0.44,0.43,0.42,0.41,0.40],
    [0.50,0.50,0.49,0.48,0.47,0.45,0.43,0.42]],

  // 모드별 고정 오버라이드 (null=LUT 추종 / 숫자=강제 front_ratio)
  awd_mode_override: { ECO: null, NORMAL: null, SPORT: null, SNOW: null, OFF_ROAD: null },
  // TV(Torque Vectoring) 좌우 게인
  tv_yaw_gain: 800,         // [Nm/(rad/s)] — 공통 fallback
  tv_threshold_rads: 0.05,  // ESC 개입 임계값 — 공통 fallback
  // [A08] 모드별 TV/ESC 독립 파라미터
  tv_yaw_gain_eco:      600,   tv_threshold_eco:      0.08,
  tv_yaw_gain_normal:   800,   tv_threshold_normal:   0.05,
  tv_yaw_gain_sport:   1200,   tv_threshold_sport:    0.03,
  tv_yaw_gain_snow:     400,   tv_threshold_snow:     0.10,
  tv_yaw_gain_offroad:  600,   tv_threshold_offroad:  0.07,

  // ══════════ CALIBRATION v1: Friction Brake Distribution ══════════
  friction_calib_enable: false,
  // 회생-유압 블렌딩 LUT: BPP(%) × 차속[km/h] → 회생비율 [0~1]
  fric_brake_v_pts:   [0, 30, 60, 100, 130],
  fric_brake_bpp_pts: [0, 10, 25, 50, 75, 100],
  fric_regen_lut:     [[0,0,0,0,0],[0.90,0.85,0.70,0.50,0.30],[0.85,0.80,0.65,0.45,0.25],[0.75,0.70,0.55,0.38,0.20],[0.65,0.60,0.48,0.32,0.16],[0.55,0.50,0.40,0.28,0.14]],
  // EBD (Electronic Brake Distribution) 전후 분배
  ebd_enable: true,
  ebd_front_ratio_static: 0.65,   // 정적 전륜 제동 비율
  ebd_cg_height_m: 0.55,          // 무게중심 높이 [m]
  ebd_wheelbase_m: 2.87,          // 축거 [m]
  // 유압 응답 시간
  brake_pressure_rise_ms: 80,     // 유압 빌드업 지연 [ms]
  regen_to_friction_ms: 50,       // 회생→유압 전환 시간 [ms]
  abs_pressure_limit_bar: 120,    // ABS 개입 시 최대 압력 [bar]

  // ══════════ CALIBRATION v1: Arbitration Limits ══════════
  // (이미 일부 존재, 통합 패널용 명시적 노출)
  arb_dischg_limit_kw: 150,   // 방전 한계 [kW]
  arb_chg_limit_kw: 70,       // 충전 한계 [kW]

  // SNOW mode: 저응답·저토크·고회생 (빙판 안전)
  snow_pedal_sensitivity: 0.4,     // 매우 완만한 응답
  snow_max_torque_ratio: 0.55,     // 저토크 (슬립 방지)
  snow_regen_strength: 0.3,        // 낮은 회생 (잠김 방지)
  // OFF_ROAD mode: 중응답·중토크·중회생 + 토크벡터링 (험로 오프로드)
  offroad_pedal_sensitivity: 0.75,  // 완만한 응답 (슬립 방지)
  offroad_max_torque_ratio: 1.0,    // 기본 토크 (100%)
  offroad_regen_strength: 0.6,      // 중간 회생 (험로 제동 안정)

  // ══════════ [Advance02] 토크 응답성 파라미터 (Torque Response) ══════════
  // 1차 저역통과 필터 시상수 [s] — 모드별 독립 설정
  // 물리적 근거: PMSM 전류 루프 응답 20~50ms, 슬루레이트는 실제 드라이브라인 관성 반영
  tau_torque_eco:      0.08,   // ECO: 완만한 응답 80ms (승차감 우선)
  tau_torque_normal:   0.04,   // NORMAL: 40ms (균형)
  tau_torque_sport:    0.02,   // SPORT: 20ms (스포티)
  tau_torque_snow:     0.12,   // SNOW: 120ms (미끄럼 방지)
  tau_torque_offroad:  0.06,   // OFF_ROAD: 60ms (험로 충격 완화)
  // 토크 슬루레이트 제한 [N·m/s] — 급격한 토크 변화 방지 (저크 억제)
  torque_slew_rate_eco:      500,    // ECO: 완만
  torque_slew_rate_normal:   1500,   // NORMAL: 표준
  torque_slew_rate_sport:    3000,   // SPORT: 빠름
  torque_slew_rate_snow:     300,    // SNOW: 최완만 (슬립 방지)
  torque_slew_rate_offroad:  800,    // OFF_ROAD: 중간
  // 인버터 디지털 처리 지연 [ms] (제어기 연산 + 게이트 드라이버)
  inverter_delay_ms: 1.0,           // 일반적 SiC 인버터: 0.5~2ms
  // 전류 루프 제어 대역폭 [Hz] (참고용)
  current_loop_bw_hz: 1000,         // 500~2000Hz (PMSM 표준)
  regen_coast_enable: true,          // [v5] Coast 중 회생제동 활성화
  regen_threshold: 0.02,             // [v5] 이 미만의 throttle에서 회생 진입

  // ── [v5-bugfix2] ESC / 횡방향 제어 파라미터 ────────────────────────────
  delta_steer_rad: 0.0,              // 조향각 [rad] (직진=0)
  lateral_enable: true,              // 횡방향 동역학 활성화
  esc_yaw_threshold: 0.05,           // ESC 개입 요레이트 오차 임계값 [rad/s]
  esc_torque_cut_gain: 0.5,          // ESC 토크컷 게인
  esc_brake_gain: 800,               // ESC 요모멘트 게인 [Nm/(rad/s)]

  // ── [v5-bugfix2] MTPV / 약계자 파라미터 ────────────────────────────────
  fw_enable: true,                   // 약계자 활성화
  mtpv_enable: true,                 // MTPV 활성화
  fw_v_margin: 0.95,                 // 전압 여유율 (Vmax = Vdc * margin / √3)

  // ── [v5-bugfix2] 2RC 배터리 등가회로 파라미터 ──────────────────────────
  R0_base: 0.020,                    // 직렬 저항 기본값 [Ω]
  R1_base: 0.015,                    // RC1 저항 [Ω]
  R2_base: 0.010,                    // RC2 저항 [Ω]
  C1_base: 500,                      // RC1 커패시턴스 [F]
  C2_base: 5000,                     // RC2 커패시턴스 [F]
  k_R0_soc_low: 0.5,                 // 저SOC 저항 증가 계수
  k_R0_soc_high: 0.3,                // 고SOC 저항 증가 계수

  // ── [v5-bugfix2] 에너지 체인 디레이팅 기준 ──────────────────────────────
  P_dis_ref: 250000,                 // 배터리 방전 기준 출력 [W] (디레이팅 % 계산용)
  // SPORT mode
  sport_pedal_sensitivity: 1.0,
  sport_max_torque_ratio: 1.15,    // 오버부스트 (115%)
  sport_regen_strength: 1.0,
  // ══════════ NEW v3: Regen Map ══════════
  // 회생제동 강도 맵 (속도별, 개도별)
  regen_map_enable: true,
  regen_low_speed_fade_kph: 15,    // 이 속도 이하에서 페이드아웃 [km/h]
  regen_max_decel_ms2: 2.5,        // 최대 회생 감속도 [m/s²]
  regen_blend_start_kph: 80,       // 고속에서 마찰 블렌딩 시작 속도 [km/h]
  regen_blend_ratio_high: 0.7,     // 고속에서 회생 비율 (나머지는 마찰)
  regen_one_pedal_level: 0.4,      // 원페달 회생 강도 (가속 0% 시)
  // ══════════ NEW v3: Driving Cycle Selection ══════════
  cycle_type: 'WLTP',              // 'WLTP' | 'FTP75' | 'EPA_HWY' | 'EPA_CITY' | 'JC08' | 'CLTC' | 'RDE_FULL' | 'RDE_URBAN' | 'RDE_RURAL' | 'RDE_MOTORWAY'
  cycle_fullspec: false,           // [A20] Full-spec 사이클 — 기본 사이클 선택시만 유효 (다중 온도/부하/페이즈 분석)
  // ══════════ NEW v3: Extended Kalman Filter ══════════
  kalman_type: 'EKF',              // 'linear' | 'EKF' | 'UKF'  [Fix-P2] EKF 기본 활성 — SOC 정확도 향상 (linear 대비 오차 ~15~30% 감소)
  ekf_alpha: 0.01,                 // EKF 비선형 상태 노이즈 보정
  ukf_alpha: 1e-3,                 // UKF Sigma-point spread parameter
  ukf_beta: 2.0,                   // UKF distribution parameter (Gaussian=2)
  ukf_kappa: 0.0,                  // UKF secondary scaling parameter
  // ══════════ NEW v3: Tire Lateral (Combined Slip Pacejka) ══════════
  tire_lateral_enable: true,       // 횡방향 타이어 모델
  tire_pC1_lat: 1.30,             // Pacejka lateral C factor
  tire_pD1_lat: 1.00,             // Pacejka lateral D1
  tire_pD2_lat: -0.08,            // Pacejka lateral D2
  tire_pK1_lat: 22.0,             // Pacejka lateral K1 (cornering stiffness)
  tire_pK2_lat: 0.7,              // Pacejka lateral K2
  tire_pE1_lat: -0.5,             // Pacejka lateral E1
  tire_pE2_lat: 0.0,              // Pacejka lateral E2
  // Rolling dynamics / rotational inertia
  I_wheel: 1.5,                    // 휠 관성 [kg·m²] (per wheel)
  I_driveshaft: 0.08,             // 드라이브샤프트 관성 [kg·m²]
  rotational_inertia_enable: true, // 회전 관성 반영
  // Tire pressure
  tire_pressure_crr_coeff: 0.008, // 공기압 저하 시 Crr 증가 계수
  // Combined slip
  combined_slip_enable: true,      // 결합 미끄럼 모델
  combined_slip_eps: 0.001,        // Numerical regularization

  // ══════════════════════════════════════════════════════
  // [v7] 노면 거칠기 (IRI 기반)
  // ══════════════════════════════════════════════════════
  k_IRI_crr: 0.00015,              // IRI → Crr 환산 계수 (Nakajima 2006 기반)
  k_IRI_vib: 0.000008,             // IRI 속도의존 진동 손실 계수

  // ══════════════════════════════════════════════════════
  // [v7] {e("타이어 공기압","Tyre Pressure")} 상세
  // ══════════════════════════════════════════════════════
  tire_pressure_crr_alpha: 0.008,  // ΔCrr/Δbar 선형 계수
  tire_pressure_crr_beta:  0.0003, // ΔCrr/Δbar² 2차 계수

  // ══════════════════════════════════════════════════════
  // [v7] Li-plating / SEI 성장 모델
  // ══════════════════════════════════════════════════════
  sei_enable: true,                // SEI 성장 모델 활성화
  k_SEI_A: 2.5e-7,                 // SEI 성장 pre-exponential [m²/s^0.5]
  k_SEI_Ea: 30000,                 // SEI 활성화 에너지 [J/mol]
  k_plating_threshold: 0.0,        // Li-plating 음극 전위 임계 [V]
  R_sei_init: 0.001,               // 초기 SEI 저항 [Ω]
  R_sei_max: 0.05,                 // 최대 SEI 저항 [Ω]

  // ══════════════════════════════════════════════════════
  // [v7] 비선형 regen/마찰 블렌딩
  // ══════════════════════════════════════════════════════
  regen_blend_k_nonlin: 2.5,       // S-커브 가파름 계수
  regen_blend_v_lo: 5.0,           // 저속 fade-out 시작 [m/s]
  regen_blend_v_hi: 15.0,          // 저속 fade-out 완료 [m/s]
  friction_brake_max_torque: 800,  // 기계 마찰 브레이크 최대 토크 [Nm]
  regen_max_jerk: 3.0,             // 최대 제동 저크 [m/s³] (승차감)

  // ══════════════════════════════════════════════════════
  // [v7] 기어박스 온도별 손실 맵
  // ══════════════════════════════════════════════════════
  gear_T_ref: 80,                  // 기어오일 기준 온도 (°C)
  gear_k_viscosity: 0.0015,        // 저온 점도 손실 계수 (저온에서 손실 증가)
  gear_k_bearing: 0.00008,         // 베어링 속도의존 손실 계수 [W/(rad/s)]
  gear_k_mesh: 0.0012,             // 치합 부하의존 손실 계수 [W/Nm]
  gear_T_oil: 80,                  // 기어오일 초기 온도 (°C)
  gear_C_th: 2000,                 // 기어박스 열용량 [J/°C]
  gear_R_th: 0.15,                 // 기어박스 열저항 [°C/W]

  // ══════════════════════════════════════════════════════
  // [v7] HVAC 상세 (히트펌프 COP 모델)
  // ══════════════════════════════════════════════════════
  hvac_cop_heat_base: 2.5,         // 난방 COP 기준값 (T_amb=0°C)
  hvac_cop_cool_base: 3.0,         // 냉방 COP 기준값 (T_amb=35°C)
  hvac_cop_temp_coeff: 0.035,      // 온도에 따른 COP 변화 계수
  hvac_base_power: 300,            // 기본 전장 부하 [W]
  hvac_max_power: 4500,            // HVAC 최대 전력 [W]
  hvac_speed_coeff: 0.0,           // 속도의존 추가 부하 [W/(m/s)] (냉각팬 등)

  // ══════════════════════════════════════════════════════
  // [v7] μ 추정 재귀 최소제곱 (RLS)
  // ══════════════════════════════════════════════════════
  rls_forgetting: 0.97,            // 망각 인자 λ
  rls_P_init: 1.0,                 // 초기 추정 공분산
  mu_min_clip: 0.2,                // μ 최소 클리핑
  mu_max_clip: 1.1,                // μ 최대 클리핑

  // ══════════════════════════════════════════════════════
  // [v7] 냉각 유량 최적화
  // ══════════════════════════════════════════════════════
  coolant_flow_opt_enable: true,   // 냉각 유량 최적화 활성화
  coolant_flow_base: 10.0,         // 기준 유량 (L/min)
  coolant_flow_min: 3.0,           // 최소 유량 (L/min)
  coolant_flow_max: 25.0,          // 최대 유량 (L/min)
  coolant_pump_power_coeff: 12.0,  // 펌프 전력 계수 [W/(L/min)]
  coolant_T_target: 45,            // 목표 냉각수 온도 (°C)
  coolant_T_alarm: 65,             // 냉각 알람 온도 (°C)

  // ══════════════════════════════════════════════════════
  // [v7] Pass/Fail 검증 기준
  // ══════════════════════════════════════════════════════
  pf_launch100_max: 8.0,           // 0→100 최대 허용 시간 [s]
  pf_launch100_min: 2.0,           // 0→100 최소 시간 (물리 하한) [s]
  pf_coast_min: 20.0,              // coast 최소 시간 [s]
  pf_wltp_max_kWh: 30.0,           // WLTP 최대 전비 [kWh/100km]
  pf_wltp_min_kWh: 8.0,            // WLTP 최소 전비 [kWh/100km]
  pf_regen_min_pct: 10.0,          // 회생 최소 비율 (%)
  pf_eta_total_min: 70.0,          // 파워트레인 최소 효율 (%)
  pf_T_motor_max: 120,             // 모터 최대 허용 온도 (°C)
  pf_T_batt_max: 45,               // 배터리 최대 허용 온도 (°C)

  // ══════════════════════════════════════════════════════
  // [v7] SOH 계수 라이브러리 참조
  // ══════════════════════════════════════════════════════
  soh_profile: 'nmc_622',          // 셀 화학계 프로파일

  // ══════════════════════════════════════════════════════
  // [enhanced] 추가 파라미터
  // ══════════════════════════════════════════════════════
  // 2. 공기/구름/등판 저항
  road_roughness_k: 0.0008,        // PSD 기반 동적 Crr 계수
  wind_direction: 0,               // 풍향 (degree, 0=정면)
  wind_speed: 0,                   // 풍속 (m/s)
  rho_air: 1.225,                  // 공기 밀도 (kg/m³)
  grade_profile_enable: false,     // θ(t) 프로파일 사용 여부

  // 4. IPMSM / FOC
  overmod_enable: false,
  pwm_mode: 'SVPWM',            // 변조 방식: 'SVPWM' | 'DPWM1' | 'DPWM0' | 'DPWM_MAX'
  dpwm_m_threshold: 0.5,        // DPWM 전환 m_a 하한  // [A21-20] M3·I6 공통 기본값에 맞춰 false로 통일            // 과변조(overmodulation) 활성화
  deadtime_compensation_enable: true, // Dead-time 보상
  dq_convergence_check: true,      // dq 루프 수렴성 검사
  Ld: 0.00025,                     // d축 인덕턴스 (H)
  Lq: 0.00035,                     // q축 인덕턴스 (H)
  Rs: 0.012,                       // 고정자 저항 (Ω)
  poles: 8,                        // 극 수 (전기각 = 기계각 × poles/2). [Fix-Steinmetz] DEFAULT_PARAMS 누락으로 omega_e=NaN → P_iron=NaN 발생하던 버그 수정
  V_dc_max: 420,                   // DC 링크 과전압 보호 한계 (V) — V_dc × 1.05 기본
  V_dc_min: 250,                   // DC 링크 저전압 차단 한계 (V) — 방전 종지
  C_dc: 1500e-6,                   // DC 링크 커패시터 용량 (F) — 1500 µF 일반적
  dc_link_dynamics_enable: false,  // DC 링크 동적 전압 연산 활성화
  current_limit: 600,              // 최대 전류 (A peak)
  deadtime_us: 2.0,                // Dead-time (μs)

  // 7. ESC / TCS
  K_p_yaw: 1200,                   // Yaw-rate P 게인
  K_i_yaw: 600,                    // Yaw-rate I 게인
  tcs_slip_target: 0.12,           // TCS 슬립 목표율
  esc_enable: true,                // ESC 활성화
  tv_enable: false,                // 토크 벡터링 활성화
  wheelbase: 2.875,                // 휠베이스 (m)
  track_width: 1.65,               // 트랙 폭 (m)
  l_f: 1.400,                      // 전축→무게중심 거리 (m)
  l_r: 1.475,                      // 후축→무게중심 거리 (m)

  // 11. 타이어 (Pacejka + Thermal)
  tire_thermal_enable: true,       // 타이어 온도 모델
  tire_temp_coeff_D: 0.0015,       // 온도→D계수 변화율
  tire_temp_optimal: 80,           // 최적 타이어 온도 (°C)
  combined_slip_mode: 'dugoff_circle', // 복합 슬립 모드
  tire_mass: 10,                   // 타이어 질량 (kg, 1개)
  tire_cp: 1200,                   // 타이어 비열 (J/kg°C)

  // 15. BMS
  bms_balance_current: 1.5,        // 밸런싱 전류 (A)
  bms_balance_mode: 'active',      // 'passive' | 'active'
  bms_fault_latch: true,           // 결함 래치
  bms_balance_threshold: 0.010,    // 밸런싱 전압 편차 기준 (V)
  bms_cell_v_max: 4.20,            // 과전압 기준 (V)
  bms_cell_v_min: 2.80,            // 저전압 기준 (V)
  bms_t_hot_cutoff: 55,            // 고온 충전 차단 온도 (°C)
  bms_t_cold_derate: 5,            // 저온 디레이팅 온도 (°C)
  bms_i_charge_max: 200,           // 최대 충전 전류 (A)

  // 16. 민감도/검증
  monte_carlo_runs: 2000,          // Monte Carlo 실행 횟수
  sensitivity_method: 'sobol',     // 'montecarlo' | 'sobol' | 'lhs'
  mc_seed: 42,                     // 난수 시드

  // ══════════════════════════════════════════════════════
  // [R-08] v7 Road & Env 메타 기본값 (roadenv 탭 및 resistanceForcesV3 에서 사용)
  // ══════════════════════════════════════════════════════
  IRI: 2.5,                         // 노면 거칠기 [m/km] 고속도로≈2.0 / 도심≈3.0
  tire_pressure_nominal: 2.4,       // 정격 공기압 [bar]
  tire_pressure_actual: 2.4,        // 실제 공기압 [bar]
  road_roughness_enable: false,     // IRI 저항 활성화
  hvac_mode: 'auto',                // 'auto' | 'heat' | 'cool'
  gear_loss_temp_enable: false,     // 기어오일 온도 손실 모델
  regen_blend_enable: false,        // 비선형 regen/마찰 S-커브 블렌딩
  // ── regen_blend_enable=ON 시 사용되는 블렌딩 적합 파라미터 ──────────────
  // (기본값: regen_blend_v_lo/hi/k_nonlin은 이미 VEHICLE_PRESETS에 정의됨)
  tau_hyd_brake:    0.20,   // 유압 브레이크 시상수 [s] — 캘리퍼 빌드업 지연
  tau_regen_brake:  0.02,   // 회생제동 시상수 [s]   — 모터 응답 지연
  regen_optimizer_enable: false,  // 회생 에너지 최적화 (ADP 기반)
  rls_mu_enable: false,             // RLS μ 추정 활성화

  // ══════════════════════════════════════════════════════
  // [FS] 기능 안전 / HARA
  // ══════════════════════════════════════════════════════
  hara_enable: true,               // HARA 모듈 활성화
  fault_injection_enable: true,    // 고장 주입 엔진
  asils: ["QM","A","B","C","D"],  // ASIL 레벨
  simulation_per_hazard: 500,      // 해저드별 시뮬 횟수
  hara_vehicle_speed_kmh: 120,     // HARA 기준 차량 속도
  hara_exposure_hours: 1000,       // 연간 노출 시간

  // [Disp13] Advanced Analytics
  psd_level: 1.0, wind_speed_adv: 5.0, cooling_flow_lpm: 10.0,
  brake_regen_ratio: 0.7, disc_init_temp: 25.0,
  v_max_chart: 300,   // R0/R2/R3 그래프 표시 최고속도 [km/h] — 차량 프리셋에서 오버라이드 [A21-12-01]
  // [A21-11-05] 소프트웨어 속도 제한 — 실제 차량 ECU 속도 캡 (0=제한없음)
  v_max_sw: 0,        // [km/h] 소프트웨어 제한 속도 (0 = 비활성화)

  // ══════════════════════════════════════════════════════
  // [Advance34] 코드에서 참조되지만 기존에 정의 누락된 파라미터
  // ══════════════════════════════════════════════════════
  CdA_cool_open: 0.04,        // R3 냉각기 개방 시 추가 CdA [m²] — rlBuildR3 시나리오 기준값
  cold_derate_factor: 0.72,   // 저온(-10°C) 배터리 디레이팅 계수 [0~1] — L1 발진 저온 시나리오
};

// ── TESLA_M3_PARAMS ──
const TESLA_M3_PARAMS = {
  name: "Tesla Model 3 LR AWD", shortName: "M3", color: "#ef4444",
  motor_config: 'dual',          // [A35] 듀얼 모터 AWD
  motor_front_power_ratio: 0.24, // 전륜 62kW / 총 258kW ≈ 24%
  awd_enable: true,              // 듀얼 모터 → AWD 활성화
  torque_split_front: 0.40,      // 전륜 40% 후륜 60%
  // [A29-03] Tesla Model 3는 원페달 드라이빙(One-Pedal) 기본 지원 — 초기값 ON
  one_pedal_enable: true,        // Tesla Model 3 기본 활성화
  regen_one_pedal_level: 0.5,   // Tesla 기본 회생 강도 (50%)
  // Vehicle — 1830 kg (AWD curb), Cd 0.23 × A 2.13 = CdA 0.49
  m: 1830, r_wheel: 0.33, CdA: 0.49, Crr: 0.0095, rho: 1.2, mu: 1.0,
  // ✅ [FIX] Tesla M3 AWD 다운포스: 플랫 배터리 바닥 + 하부 에어로 패키지
  ClA: -0.04,             // 양력면적 [m²] — 소형 능동 에어로 효과 (실측 참고값)
  aero_balance_f: 0.38,   // 전륜 38% / 후륜 62% (M3 프론트 스포일러 기여)
  // Powertrain — Dual motor combined: ~258 kW wheel, ~493 Nm combined
  // Front: PM motor ~62 kW, Rear: PM motor ~220 kW (gear_ratio weighted avg 9.3)
  // [A21-11-06] P_max_wheel: 300000→258000 W (LR AWD 휠 출력 실측 기준)
  gear_ratio: 9.3, eta_driveline: 0.95, T_max_motor: 493, P_max_wheel: 258000,
  // Battery — 75 kWh, 400V pack, peak discharge ~340 kW
  P_max_battery: 340000, P_max_regen: 70000, battery_energy: 75, soc_init: 0.9,
  eta_motor_inv: 0.91, aux_power: 1500,
  // Motor IPMSM — equivalent single motor for dual-motor combined system
  Rs: 0.015, Ld: 0.0003, Lq: 0.0006, lambda_pm: 0.12, poles: 8, J_motor: 0.05,
  V_dc: 400, I_max_rms: 700, m_a: 1.0,
  // Environment
  // Battery detailed
  R_batt: 0.08, Q_nom: 200, batt_temp: 25,
  // Inverter - CORRECTED for realistic SiC MOSFET (Tesla uses SiC in newer models)
  // Motor loss
  k_h: 0.02, k_e: 0.0001, k_m0: 20, k_m1: 0.01, k_m2: 0.000005,
  // Gear loss
  k_g0: 30, k_g1: 0.002, k_g2: 0.00001,
  // Thermal
  C_th_inv: 2200, R_th_ic: 0.07, k_inv_temp: 0.003,
  C_th_motor: 13000, R_th_mc: 0.12, alpha_Cu: 0.0039, k_lambda: 0.001, T_ref: 25,
  C_th_cool: 15000, UA_rad: 200, phi_speed: 0.5, P_pump: 150, P_fan: 100,
  T_inv_derate_start: 95, T_inv_derate_end: 110,
  T_motor_derate_start: 140, T_motor_derate_end: 160,
  P_dis_ref: 340000, P_chg_ref: 70000,
  // Inverter Controller — FOC / Vector Control
  // [v9-fix1] IMC 설계: BW_i≈1000Hz (고성능), Rs=12mΩ, Ld=0.25mH, Lq=0.5mH
  Kp_id: 1.57, Ki_id: 75, Kp_iq: 3.14, Ki_iq: 75, // d/q-axis current PI
  Kp_spd: 130, Ki_spd: 2700, // speed loop PI (J_eq≈2.07, BW=10Hz)
  Id_ref_base: 0, // base d-axis current reference (A)
  dead_time: 2.5e-6, // inverter dead-time (s)
  ctrl_dt: 0.0001, // control loop period (s)
  carrier_freq: 10000, // PWM carrier freq (Hz)
  torque_rate_limit: 5000, // Nm/s max torque slew
  // BMS — Battery Management System
  bms_i_charge_max: 300, bms_i_discharge_max: 900, // pack current limits (A) — 340kW/400V ≈ 850A
  bms_soc_charge_cutoff: 0.98, bms_soc_discharge_cutoff: 0.05, // SOC window
  bms_t_discharge_min: -20, bms_t_discharge_max: 60, // discharge temp window
  bms_cell_count_s: 96, bms_cell_count_p: 4, // cell configuration
  bms_balance_threshold: 0.02, // V — cell balancing delta threshold
  bms_precharge_v: 350, // precharge target voltage (V)
  bms_regen_soc_taper_start: 0.85, // SOC above which regen tapers
  bms_cold_derate_temp: 5, bms_cold_derate_factor: 0.5, // cold power derating
  // Correlation K-Factors
  v_max_chart: 300,   // R0/R2/R3 그래프 최고속도 [km/h] [A21-12-01]
  // [A21-11-05] 소프트웨어 속도 제한 — Tesla Model 3 LR AWD Highland 이전 기준
  v_max_sw: 250,      // [km/h] 소프트웨어 제한 (Highland: 201, Pre-Highland LR: 225~250)
};

// ── TESLA_M3_RWD_PARAMS ──
const TESLA_M3_RWD_PARAMS = {
  name: "Tesla Model 3 SR RWD", shortName: "M3R", color: "#ef4444",
  motor_config: 'single_rear',   // [A35] 싱글 리어 모터
  motor_front_power_ratio: 0,
  awd_enable: false,
  // Vehicle — 1750 kg (SR RWD curb), same aero
  m: 1752, r_wheel: 0.33, CdA: 0.49, Crr: 0.0095, rho: 1.2, mu: 1.0,
  // ✅ [FIX] M3 RWD: AWD와 동일 차체 → 동일 공력 세팅
  ClA: -0.04, aero_balance_f: 0.38,
  // Powertrain — Single rear motor 208 kW / 340 Nm
  gear_ratio: 9.0, eta_driveline: 0.95, T_max_motor: 340, P_max_wheel: 208000,
  // Battery — 60 kWh (LFP), 400V
  P_max_battery: 240000, P_max_regen: 60000, battery_energy: 60, soc_init: 0.9,
  eta_motor_inv: 0.92, aux_power: 1200,
  Rs: 0.018, Ld: 0.00035, Lq: 0.00065, lambda_pm: 0.11, poles: 8, J_motor: 0.04,
  V_dc: 400, I_max_rms: 580, m_a: 1.0,
  R_batt: 0.07, Q_nom: 155, batt_temp: 25,
  k_h: 0.02, k_e: 0.0001, k_m0: 18, k_m1: 0.009, k_m2: 0.000004,
  k_g0: 28, k_g1: 0.0018, k_g2: 0.000009,
  C_th_inv: 1800, R_th_ic: 0.09, k_inv_temp: 0.003,
  C_th_motor: 11000, R_th_mc: 0.14, alpha_Cu: 0.0039, k_lambda: 0.001, T_ref: 25,
  C_th_cool: 12000, UA_rad: 160, phi_speed: 0.5, P_pump: 120, P_fan: 80,
  T_inv_derate_start: 95, T_inv_derate_end: 110,
  T_motor_derate_start: 140, T_motor_derate_end: 160,
  P_dis_ref: 240000, P_chg_ref: 60000,
  Kp_id: 1.58, Ki_id: 76, Kp_iq: 3.16, Ki_iq: 76,
  Kp_spd: 120, Ki_spd: 2500,
  Id_ref_base: 0, dead_time: 2.5e-6, ctrl_dt: 0.0001, carrier_freq: 10000,
  torque_rate_limit: 4000,
  bms_i_charge_max: 200, bms_i_discharge_max: 650,
  bms_soc_charge_cutoff: 1.0, bms_soc_discharge_cutoff: 0.05,
  bms_t_discharge_min: -20, bms_t_discharge_max: 60,
  bms_cell_count_s: 96, bms_cell_count_p: 4,
  bms_cold_derate_temp: 5, bms_cold_derate_factor: 0.55,
  v_max_chart: 280, v_max_sw: 225,
  one_pedal_enable: true, regen_one_pedal_level: 0.5,
};

// ── IONIQ6_PARAMS ──
const IONIQ6_PARAMS = {
  name: "Ioniq 6 LR AWD", shortName: "I6", color: "#3b82f6",
  motor_config: 'dual',          // [A35] 듀얼 모터 AWD
  motor_front_power_ratio: 0.29, // 전륜 74kW / 총 239kW ≈ 29% (현대 E-GMP 리어바이어스)
  awd_enable: true,
  torque_split_front: 0.31,      // 전륜 31% 후륜 69% (E-GMP 기본)
  // Vehicle — CdA = Cd(0.21) × A(2.25) = 0.4725
  m: 1985, r_wheel: 0.335, CdA: 0.4725, Crr: 0.011, rho: 1.2, mu: 1.0,
  // ✅ [FIX] IONIQ 6 다운포스: 능동 에어 플랩 + 최적화된 언더바디 (세계 최저 Cd)
  ClA: -0.06,             // 양력면적 [m²] — 능동 에어로 플랩 효과 포함 (실측 참고값)
  aero_balance_f: 0.36,   // 전 36% / 후 64%
  // Powertrain — AWD system: 239 kW / 605 Nm, rear-biased
  gear_ratio: 9.0, eta_driveline: 0.96, T_max_motor: 605, P_max_wheel: 239000,
  // Battery — E-GMP 800V, 77.4 kWh
  P_max_battery: 260000, P_max_regen: 150000, battery_energy: 77.4, soc_init: 0.9,
  eta_motor_inv: 0.93, aux_power: 1500,
  // Motor IPMSM — 800V system, lower current higher voltage
  // lambda_pm calibrated for 605 Nm peak with reluctance torque
  Rs: 0.028, Ld: 0.0005, Lq: 0.0010, lambda_pm: 0.135, poles: 8, J_motor: 0.06,
  V_dc: 680, I_max_rms: 420, m_a: 1.0,
  // Environment
  // Battery — 800V pack, lower Ri due to cell arrangement
  R_batt: 0.045, Q_nom: 110, batt_temp: 25,
  // Inverter — SiC, higher efficiency
  // Motor loss — dual motor, oil-cooled, slightly different loss profile
  k_h: 0.018, k_e: 0.00008, k_m0: 25, k_m1: 0.012, k_m2: 0.000006,
  // Gear loss
  k_g0: 35, k_g1: 0.0025, k_g2: 0.000012,
  // Thermal — integrated thermal management, heat pump
  C_th_inv: 2500, R_th_ic: 0.06, k_inv_temp: 0.002,
  C_th_motor: 14000, R_th_mc: 0.12, alpha_Cu: 0.0039, k_lambda: 0.001, T_ref: 25,
  C_th_cool: 18000, UA_rad: 250, phi_speed: 0.5, P_pump: 180, P_fan: 120,
  T_inv_derate_start: 100, T_inv_derate_end: 115,
  T_motor_derate_start: 145, T_motor_derate_end: 165,
  P_dis_ref: 260000, P_chg_ref: 150000,
  // Inverter Controller — FOC / Vector Control (800V tuning)
  // [v9-fix1] IMC 설계: BW_i≈700Hz (800V system), Ld=0.35mH, Lq=0.7mH, Rs=20mΩ
  Kp_id: 1.54, Ki_id: 88, Kp_iq: 3.08, Ki_iq: 88,
  Kp_spd: 155, Ki_spd: 3250, // J_eq≈2.47 (AWD 질량 2130kg)
  Id_ref_base: 0,
  dead_time: 1.5e-6, // SiC allows shorter dead-time
  ctrl_dt: 0.00005, // faster control loop (800V SiC)
  carrier_freq: 12000,
  torque_rate_limit: 6000,
  // BMS — Battery Management System (800V E-GMP)
  bms_i_charge_max: 250, bms_i_discharge_max: 450,
  bms_soc_charge_cutoff: 0.97, bms_soc_discharge_cutoff: 0.04,
  bms_t_discharge_min: -25, bms_t_discharge_max: 60,
  bms_cell_count_s: 192, bms_cell_count_p: 2, // 800V: more series
  bms_balance_threshold: 0.015,
  bms_precharge_v: 600,
  bms_regen_soc_taper_start: 0.82,
  bms_cold_derate_temp: 3, bms_cold_derate_factor: 0.4,
  // Correlation K-Factors
  v_max_chart: 185,   // R2/R3 그래프 최고속도 [km/h]
  // [A21-11-05] 소프트웨어 속도 제한 — Ioniq 6 LR AWD 공식 최고속도 185 km/h
  v_max_sw: 185,      // [km/h] 현대 공식 소프트웨어 제한 (185 km/h)
};

// ── IONIQ6_RWD_PARAMS ──
const IONIQ6_RWD_PARAMS = {
  name: "Ioniq 6 LR RWD", shortName: "I6R", color: "#3b82f6",
  motor_config: 'single_rear',   // [A35] 싱글 리어 모터
  motor_front_power_ratio: 0,
  awd_enable: false,
  // Vehicle — 1820 kg (RWD curb)
  m: 1820, r_wheel: 0.335, CdA: 0.4725, Crr: 0.011, rho: 1.2, mu: 1.0,
  // ✅ [FIX] IONIQ 6 RWD: AWD와 동일 차체 → 동일 공력 세팅
  ClA: -0.06, aero_balance_f: 0.36,
  // Powertrain — Single rear motor 168 kW / 350 Nm (E-GMP 800V RWD)
  gear_ratio: 9.7, eta_driveline: 0.96, T_max_motor: 350, P_max_wheel: 168000,
  // Battery — 77.4 kWh 800V
  P_max_battery: 200000, P_max_regen: 150000, battery_energy: 77.4, soc_init: 0.9,
  eta_motor_inv: 0.94, aux_power: 1400,
  Rs: 0.030, Ld: 0.00055, Lq: 0.0011, lambda_pm: 0.13, poles: 8, J_motor: 0.055,
  V_dc: 680, I_max_rms: 300, m_a: 1.0,
  R_batt: 0.048, Q_nom: 110, batt_temp: 25,
  k_h: 0.018, k_e: 0.00008, k_m0: 22, k_m1: 0.011, k_m2: 0.000005,
  k_g0: 32, k_g1: 0.0022, k_g2: 0.000011,
  C_th_inv: 2200, R_th_ic: 0.07, k_inv_temp: 0.002,
  C_th_motor: 12500, R_th_mc: 0.13, alpha_Cu: 0.0039, k_lambda: 0.001, T_ref: 25,
  C_th_cool: 16000, UA_rad: 220, phi_speed: 0.5, P_pump: 160, P_fan: 100,
  T_inv_derate_start: 100, T_inv_derate_end: 115,
  T_motor_derate_start: 145, T_motor_derate_end: 165,
  P_dis_ref: 200000, P_chg_ref: 150000,
  Kp_id: 1.52, Ki_id: 86, Kp_iq: 3.04, Ki_iq: 86,
  Kp_spd: 145, Ki_spd: 3000,
  Id_ref_base: 0, dead_time: 1.5e-6, ctrl_dt: 0.00005, carrier_freq: 12000,
  torque_rate_limit: 5000,
  bms_i_charge_max: 220, bms_i_discharge_max: 350,
  bms_soc_charge_cutoff: 0.97, bms_soc_discharge_cutoff: 0.04,
  bms_t_discharge_min: -25, bms_t_discharge_max: 60,
  bms_cell_count_s: 192, bms_cell_count_p: 2,
  bms_cold_derate_temp: 3, bms_cold_derate_factor: 0.42,
  v_max_chart: 185, v_max_sw: 185,
};

// ── TESLA_MY_PARAMS ──
const TESLA_MY_PARAMS = {
  name: "Tesla Model Y LR AWD", shortName: "MY", color: "#dc2626",
  motor_config: "dual", motor_front_power_ratio: 0.24, awd_enable: true,
  torque_split_front: 0.40,
  one_pedal_enable: true, regen_one_pedal_level: 0.5,
  // Vehicle — CdA = 0.227 × 2.59 ≈ 0.588
  m: 2003, r_wheel: 0.338, CdA: 0.588, Crr: 0.0098, rho: 1.2, mu: 1.0,
  l_f: 1.39, l_r: 1.50, wheel_base: 2.890, h_cg: 0.56, Iz: 3100,
  // Powertrain — 250 kW / 559 Nm combined AWD
  gear_ratio: 9.0, eta_driveline: 0.95, T_max_motor: 559, P_max_wheel: 250000,
  // Battery — 75 kWh (usable), 400V
  P_max_battery: 320000, P_max_regen: 68000, battery_energy: 75, soc_init: 0.9,
  eta_motor_inv: 0.91, aux_power: 1500,
  Rs: 0.015, Ld: 0.00030, Lq: 0.00060, lambda_pm: 0.125, poles: 8, J_motor: 0.055,
  V_dc: 400, I_max_rms: 680, m_a: 1.0,
  R_batt: 0.075, Q_nom: 195, batt_temp: 25,
  k_h: 0.020, k_e: 0.00010, k_m0: 22, k_m1: 0.010, k_m2: 0.0000050,
  k_g0: 32, k_g1: 0.0020, k_g2: 0.000010,
  C_th_inv: 2200, R_th_ic: 0.07, k_inv_temp: 0.003,
  C_th_motor: 13000, R_th_mc: 0.13, alpha_Cu: 0.0039, k_lambda: 0.001, T_ref: 25,
  C_th_cool: 16000, UA_rad: 210, phi_speed: 0.5, P_pump: 150, P_fan: 100,
  T_inv_derate_start: 95, T_inv_derate_end: 110,
  T_motor_derate_start: 140, T_motor_derate_end: 160,
  P_dis_ref: 320000, P_chg_ref: 68000,
  Kp_id: 1.51, Ki_id: 75, Kp_iq: 3.02, Ki_iq: 75,
  Kp_spd: 130, Ki_spd: 2800,
  Id_ref_base: 0, dead_time: 2.5e-6, ctrl_dt: 0.0001, carrier_freq: 10000,
  torque_rate_limit: 5000,
  bms_i_charge_max: 280, bms_i_discharge_max: 870,
  bms_soc_charge_cutoff: 0.98, bms_soc_discharge_cutoff: 0.05,
  bms_t_discharge_min: -20, bms_t_discharge_max: 60,
  bms_cell_count_s: 96, bms_cell_count_p: 4,
  bms_cold_derate_temp: 5, bms_cold_derate_factor: 0.50,
  v_max_chart: 250, v_max_sw: 217,
};

// ── TESLA_MS_PLAID_PARAMS ──
const TESLA_MS_PLAID_PARAMS = {
  name: "Tesla Model S Plaid", shortName: "MSP", color: "#b91c1c",
  motor_config: "dual", motor_front_power_ratio: 0.28, awd_enable: true,
  torque_split_front: 0.35,  // 트라이 모터 — 전1 후2 구성, 후방 바이어스
  one_pedal_enable: true, regen_one_pedal_level: 0.55,
  // Vehicle — Cd=0.208, A≈2.36, CdA≈0.491
  m: 2162, r_wheel: 0.342, CdA: 0.491, Crr: 0.0088, rho: 1.2, mu: 1.0,
  l_f: 1.33, l_r: 1.54, wheel_base: 2.960, h_cg: 0.51, Iz: 3600,
  // Powertrain — tri-motor ~760 kW / 1420 Nm (peak overboost)
  gear_ratio: 9.7, eta_driveline: 0.94, T_max_motor: 1420, P_max_wheel: 760000,
  // Battery — 95 kWh usable, 400V architecture
  P_max_battery: 820000, P_max_regen: 90000, battery_energy: 95, soc_init: 0.9,
  eta_motor_inv: 0.92, aux_power: 2000,
  Rs: 0.012, Ld: 0.00025, Lq: 0.00050, lambda_pm: 0.130, poles: 8, J_motor: 0.060,
  V_dc: 400, I_max_rms: 1100, m_a: 1.0,
  R_batt: 0.055, Q_nom: 240, batt_temp: 25,
  k_h: 0.018, k_e: 0.00009, k_m0: 28, k_m1: 0.012, k_m2: 0.0000055,
  k_g0: 28, k_g1: 0.0018, k_g2: 0.000009,
  C_th_inv: 3000, R_th_ic: 0.05, k_inv_temp: 0.003,
  C_th_motor: 16000, R_th_mc: 0.10, alpha_Cu: 0.0039, k_lambda: 0.001, T_ref: 25,
  C_th_cool: 22000, UA_rad: 300, phi_speed: 0.5, P_pump: 200, P_fan: 140,
  T_inv_derate_start: 100, T_inv_derate_end: 115,
  T_motor_derate_start: 145, T_motor_derate_end: 165,
  P_dis_ref: 820000, P_chg_ref: 90000,
  Kp_id: 1.41, Ki_id: 67, Kp_iq: 2.82, Ki_iq: 67,
  Kp_spd: 155, Ki_spd: 3200,
  Id_ref_base: 0, dead_time: 2.0e-6, ctrl_dt: 0.0001, carrier_freq: 12000,
  torque_rate_limit: 12000,
  bms_i_charge_max: 300, bms_i_discharge_max: 2100,
  bms_soc_charge_cutoff: 0.98, bms_soc_discharge_cutoff: 0.05,
  bms_t_discharge_min: -25, bms_t_discharge_max: 60,
  bms_cell_count_s: 104, bms_cell_count_p: 5,
  bms_cold_derate_temp: 5, bms_cold_derate_factor: 0.45,
  v_max_chart: 322, v_max_sw: 322,
};

// ── TESLA_MX_PARAMS ──
const TESLA_MX_PARAMS = {
  name: "Tesla Model X LR AWD", shortName: "MX", color: "#991b1b",
  motor_config: "dual", motor_front_power_ratio: 0.26, awd_enable: true,
  torque_split_front: 0.38,
  one_pedal_enable: true, regen_one_pedal_level: 0.50,
  // Vehicle — Cd=0.25, A≈2.85, CdA≈0.713
  m: 2487, r_wheel: 0.350, CdA: 0.713, Crr: 0.0102, rho: 1.2, mu: 1.0,
  l_f: 1.46, l_r: 1.48, wheel_base: 2.965, h_cg: 0.62, Iz: 4200,
  // Powertrain — dual motor ~380 kW / 660 Nm
  gear_ratio: 9.3, eta_driveline: 0.95, T_max_motor: 660, P_max_wheel: 380000,
  // Battery — 95 kWh usable, 400V
  P_max_battery: 420000, P_max_regen: 85000, battery_energy: 95, soc_init: 0.9,
  eta_motor_inv: 0.91, aux_power: 2200,
  Rs: 0.016, Ld: 0.00032, Lq: 0.00064, lambda_pm: 0.132, poles: 8, J_motor: 0.065,
  V_dc: 400, I_max_rms: 800, m_a: 1.0,
  R_batt: 0.060, Q_nom: 238, batt_temp: 25,
  k_h: 0.021, k_e: 0.00010, k_m0: 25, k_m1: 0.011, k_m2: 0.0000052,
  k_g0: 36, k_g1: 0.0022, k_g2: 0.000011,
  C_th_inv: 2500, R_th_ic: 0.065, k_inv_temp: 0.003,
  C_th_motor: 15000, R_th_mc: 0.11, alpha_Cu: 0.0039, k_lambda: 0.001, T_ref: 25,
  C_th_cool: 20000, UA_rad: 270, phi_speed: 0.5, P_pump: 180, P_fan: 130,
  T_inv_derate_start: 95, T_inv_derate_end: 110,
  T_motor_derate_start: 140, T_motor_derate_end: 160,
  P_dis_ref: 420000, P_chg_ref: 85000,
  Kp_id: 1.54, Ki_id: 76, Kp_iq: 3.08, Ki_iq: 76,
  Kp_spd: 142, Ki_spd: 2950,
  Id_ref_base: 0, dead_time: 2.5e-6, ctrl_dt: 0.0001, carrier_freq: 10000,
  torque_rate_limit: 6500,
  bms_i_charge_max: 290, bms_i_discharge_max: 1100,
  bms_soc_charge_cutoff: 0.98, bms_soc_discharge_cutoff: 0.05,
  bms_t_discharge_min: -20, bms_t_discharge_max: 60,
  bms_cell_count_s: 104, bms_cell_count_p: 5,
  bms_cold_derate_temp: 5, bms_cold_derate_factor: 0.48,
  v_max_chart: 260, v_max_sw: 250,
};

// ── IONIQ5_AWD_PARAMS ──
const IONIQ5_AWD_PARAMS = {
  name: "Ioniq 5 LR AWD", shortName: "I5A", color: "#1d4ed8",
  motor_config: "dual", motor_front_power_ratio: 0.33, awd_enable: true,
  torque_split_front: 0.32, // E-GMP 후방 바이어스
  one_pedal_enable: true, regen_one_pedal_level: 0.45,
  // Vehicle — Cd=0.288, A≈2.46, CdA≈0.708
  m: 2100, r_wheel: 0.340, CdA: 0.708, Crr: 0.0105, rho: 1.2, mu: 1.0,
  l_f: 1.40, l_r: 1.53, wheel_base: 2.900, h_cg: 0.59, Iz: 3200,
  // Powertrain — 225 kW / 605 Nm AWD (전 74kW + 후 165kW)
  gear_ratio: 9.0, eta_driveline: 0.96, T_max_motor: 605, P_max_wheel: 225000,
  // Battery — 77.4 kWh, 800V E-GMP
  P_max_battery: 245000, P_max_regen: 150000, battery_energy: 77.4, soc_init: 0.9,
  eta_motor_inv: 0.93, aux_power: 1600,
  Rs: 0.028, Ld: 0.00048, Lq: 0.00095, lambda_pm: 0.133, poles: 8, J_motor: 0.060,
  V_dc: 680, I_max_rms: 400, m_a: 1.0,
  R_batt: 0.048, Q_nom: 110, batt_temp: 25,
  k_h: 0.019, k_e: 0.000085, k_m0: 26, k_m1: 0.012, k_m2: 0.0000058,
  k_g0: 36, k_g1: 0.0024, k_g2: 0.000012,
  C_th_inv: 2500, R_th_ic: 0.062, k_inv_temp: 0.002,
  C_th_motor: 14000, R_th_mc: 0.12, alpha_Cu: 0.0039, k_lambda: 0.001, T_ref: 25,
  C_th_cool: 18000, UA_rad: 250, phi_speed: 0.5, P_pump: 180, P_fan: 120,
  T_inv_derate_start: 100, T_inv_derate_end: 115,
  T_motor_derate_start: 145, T_motor_derate_end: 165,
  P_dis_ref: 245000, P_chg_ref: 150000,
  Kp_id: 1.54, Ki_id: 88, Kp_iq: 3.08, Ki_iq: 88,
  Kp_spd: 150, Ki_spd: 3100,
  Id_ref_base: 0, dead_time: 1.5e-6, ctrl_dt: 0.00005, carrier_freq: 12000,
  torque_rate_limit: 6000,
  bms_i_charge_max: 240, bms_i_discharge_max: 430,
  bms_soc_charge_cutoff: 0.97, bms_soc_discharge_cutoff: 0.04,
  bms_t_discharge_min: -25, bms_t_discharge_max: 60,
  bms_cell_count_s: 192, bms_cell_count_p: 2,
  bms_cold_derate_temp: 3, bms_cold_derate_factor: 0.42,
  v_max_chart: 185, v_max_sw: 185,
};

// ── IONIQ5_RWD_PARAMS ──
const IONIQ5_RWD_PARAMS = {
  name: "Ioniq 5 LR RWD", shortName: "I5R", color: "#2563eb",
  motor_config: "single_rear", motor_front_power_ratio: 0, awd_enable: false,
  one_pedal_enable: true, regen_one_pedal_level: 0.45,
  m: 1985, r_wheel: 0.340, CdA: 0.708, Crr: 0.0105, rho: 1.2, mu: 1.0,
  l_f: 1.42, l_r: 1.46, wheel_base: 2.900, h_cg: 0.58, Iz: 3000,
  // Powertrain — 160 kW / 350 Nm (RWD 단독)
  gear_ratio: 9.7, eta_driveline: 0.96, T_max_motor: 350, P_max_wheel: 160000,
  P_max_battery: 195000, P_max_regen: 150000, battery_energy: 77.4, soc_init: 0.9,
  eta_motor_inv: 0.94, aux_power: 1500,
  Rs: 0.031, Ld: 0.00052, Lq: 0.00104, lambda_pm: 0.128, poles: 8, J_motor: 0.055,
  V_dc: 680, I_max_rms: 290, m_a: 1.0,
  R_batt: 0.050, Q_nom: 110, batt_temp: 25,
  k_h: 0.018, k_e: 0.000082, k_m0: 23, k_m1: 0.011, k_m2: 0.0000052,
  k_g0: 33, k_g1: 0.0021, k_g2: 0.000011,
  C_th_inv: 2200, R_th_ic: 0.070, k_inv_temp: 0.002,
  C_th_motor: 12500, R_th_mc: 0.13, alpha_Cu: 0.0039, k_lambda: 0.001, T_ref: 25,
  C_th_cool: 16000, UA_rad: 225, phi_speed: 0.5, P_pump: 160, P_fan: 105,
  T_inv_derate_start: 100, T_inv_derate_end: 115,
  T_motor_derate_start: 145, T_motor_derate_end: 165,
  P_dis_ref: 195000, P_chg_ref: 150000,
  Kp_id: 1.55, Ki_id: 89, Kp_iq: 3.10, Ki_iq: 89,
  Kp_spd: 140, Ki_spd: 2900,
  Id_ref_base: 0, dead_time: 1.5e-6, ctrl_dt: 0.00005, carrier_freq: 12000,
  torque_rate_limit: 5000,
  bms_i_charge_max: 220, bms_i_discharge_max: 340,
  bms_soc_charge_cutoff: 0.97, bms_soc_discharge_cutoff: 0.04,
  bms_t_discharge_min: -25, bms_t_discharge_max: 60,
  bms_cell_count_s: 192, bms_cell_count_p: 2,
  bms_cold_derate_temp: 3, bms_cold_derate_factor: 0.43,
  v_max_chart: 185, v_max_sw: 185,
};

// ── IONIQ5_N_PARAMS ──
const IONIQ5_N_PARAMS = {
  name: "Ioniq 5 N", shortName: "I5N", color: "#7c3aed",
  motor_config: "dual", motor_front_power_ratio: 0.34, awd_enable: true,
  torque_split_front: 0.34,
  one_pedal_enable: true, regen_one_pedal_level: 0.60,
  // Vehicle — N 에어로 패키지 적용 CdA 미세 증가
  m: 2235, r_wheel: 0.340, CdA: 0.720, Crr: 0.0102, rho: 1.2, mu: 1.0,
  l_f: 1.38, l_r: 1.52, wheel_base: 2.900, h_cg: 0.58, Iz: 3400,
  // Powertrain — 478 kW 상시 / 608 kW N-Boost 10s, 740 Nm AWD
  gear_ratio: 9.0, eta_driveline: 0.96, T_max_motor: 740, P_max_wheel: 478000,
  P_max_battery: 620000, P_max_regen: 150000, battery_energy: 84, soc_init: 0.9,
  eta_motor_inv: 0.93, aux_power: 1800,
  Rs: 0.025, Ld: 0.00044, Lq: 0.00088, lambda_pm: 0.138, poles: 8, J_motor: 0.065,
  V_dc: 680, I_max_rms: 520, m_a: 1.0,
  R_batt: 0.042, Q_nom: 120, batt_temp: 25,
  k_h: 0.017, k_e: 0.000080, k_m0: 28, k_m1: 0.013, k_m2: 0.0000060,
  k_g0: 38, k_g1: 0.0026, k_g2: 0.000013,
  C_th_inv: 3000, R_th_ic: 0.055, k_inv_temp: 0.002,
  C_th_motor: 16000, R_th_mc: 0.10, alpha_Cu: 0.0039, k_lambda: 0.001, T_ref: 25,
  C_th_cool: 20000, UA_rad: 280, phi_speed: 0.5, P_pump: 200, P_fan: 140,
  T_inv_derate_start: 105, T_inv_derate_end: 120,
  T_motor_derate_start: 150, T_motor_derate_end: 170,
  P_dis_ref: 620000, P_chg_ref: 150000,
  Kp_id: 1.50, Ki_id: 85, Kp_iq: 3.00, Ki_iq: 85,
  Kp_spd: 165, Ki_spd: 3450,
  Id_ref_base: 0, dead_time: 1.5e-6, ctrl_dt: 0.00005, carrier_freq: 12000,
  torque_rate_limit: 9000,
  bms_i_charge_max: 260, bms_i_discharge_max: 920,
  bms_soc_charge_cutoff: 0.97, bms_soc_discharge_cutoff: 0.04,
  bms_t_discharge_min: -25, bms_t_discharge_max: 60,
  bms_cell_count_s: 192, bms_cell_count_p: 2,
  bms_cold_derate_temp: 2, bms_cold_derate_factor: 0.38,
  v_max_chart: 260, v_max_sw: 260,
};

// ── KIA_EV6_AWD_PARAMS ──
const KIA_EV6_AWD_PARAMS = {
  name: "Kia EV6 LR AWD", shortName: "E6A", color: "#15803d",
  motor_config: "dual", motor_front_power_ratio: 0.31, awd_enable: true,
  torque_split_front: 0.31,
  one_pedal_enable: true, regen_one_pedal_level: 0.45,
  // Vehicle — Cd=0.228, A≈2.25, CdA≈0.513
  m: 2010, r_wheel: 0.338, CdA: 0.513, Crr: 0.0100, rho: 1.2, mu: 1.0,
  l_f: 1.39, l_r: 1.50, wheel_base: 2.900, h_cg: 0.54, Iz: 3100,
  // Powertrain — 239 kW / 605 Nm (전 74kW + 후 165kW)
  gear_ratio: 9.0, eta_driveline: 0.96, T_max_motor: 605, P_max_wheel: 239000,
  P_max_battery: 260000, P_max_regen: 150000, battery_energy: 77.4, soc_init: 0.9,
  eta_motor_inv: 0.93, aux_power: 1550,
  Rs: 0.028, Ld: 0.00050, Lq: 0.00100, lambda_pm: 0.135, poles: 8, J_motor: 0.058,
  V_dc: 680, I_max_rms: 420, m_a: 1.0,
  R_batt: 0.046, Q_nom: 110, batt_temp: 25,
  k_h: 0.018, k_e: 0.000082, k_m0: 25, k_m1: 0.012, k_m2: 0.0000057,
  k_g0: 35, k_g1: 0.0024, k_g2: 0.000012,
  C_th_inv: 2500, R_th_ic: 0.062, k_inv_temp: 0.002,
  C_th_motor: 14000, R_th_mc: 0.12, alpha_Cu: 0.0039, k_lambda: 0.001, T_ref: 25,
  C_th_cool: 18000, UA_rad: 250, phi_speed: 0.5, P_pump: 178, P_fan: 118,
  T_inv_derate_start: 100, T_inv_derate_end: 115,
  T_motor_derate_start: 145, T_motor_derate_end: 165,
  P_dis_ref: 260000, P_chg_ref: 150000,
  Kp_id: 1.54, Ki_id: 88, Kp_iq: 3.08, Ki_iq: 88,
  Kp_spd: 152, Ki_spd: 3150,
  Id_ref_base: 0, dead_time: 1.5e-6, ctrl_dt: 0.00005, carrier_freq: 12000,
  torque_rate_limit: 6000,
  bms_i_charge_max: 248, bms_i_discharge_max: 450,
  bms_soc_charge_cutoff: 0.97, bms_soc_discharge_cutoff: 0.04,
  bms_t_discharge_min: -25, bms_t_discharge_max: 60,
  bms_cell_count_s: 192, bms_cell_count_p: 2,
  bms_cold_derate_temp: 3, bms_cold_derate_factor: 0.40,
  v_max_chart: 185, v_max_sw: 185,
};

// ── KIA_EV6_GT_PARAMS ──
const KIA_EV6_GT_PARAMS = {
  name: "Kia EV6 GT", shortName: "E6G", color: "#166534",
  motor_config: "dual", motor_front_power_ratio: 0.33, awd_enable: true,
  torque_split_front: 0.33,
  one_pedal_enable: true, regen_one_pedal_level: 0.55,
  // Vehicle — GT 에어로 패키지, Cd=0.228, A≈2.25, CdA≈0.513
  m: 2155, r_wheel: 0.338, CdA: 0.520, Crr: 0.0100, rho: 1.2, mu: 1.0,
  l_f: 1.37, l_r: 1.53, wheel_base: 2.900, h_cg: 0.55, Iz: 3300,
  // Powertrain — 430 kW / 740 Nm AWD GT
  gear_ratio: 9.0, eta_driveline: 0.96, T_max_motor: 740, P_max_wheel: 430000,
  P_max_battery: 460000, P_max_regen: 150000, battery_energy: 77.4, soc_init: 0.9,
  eta_motor_inv: 0.93, aux_power: 1700,
  Rs: 0.024, Ld: 0.00043, Lq: 0.00086, lambda_pm: 0.140, poles: 8, J_motor: 0.062,
  V_dc: 680, I_max_rms: 530, m_a: 1.0,
  R_batt: 0.043, Q_nom: 110, batt_temp: 25,
  k_h: 0.017, k_e: 0.000078, k_m0: 27, k_m1: 0.013, k_m2: 0.0000060,
  k_g0: 37, k_g1: 0.0025, k_g2: 0.000012,
  C_th_inv: 2900, R_th_ic: 0.056, k_inv_temp: 0.002,
  C_th_motor: 15500, R_th_mc: 0.105, alpha_Cu: 0.0039, k_lambda: 0.001, T_ref: 25,
  C_th_cool: 19500, UA_rad: 270, phi_speed: 0.5, P_pump: 195, P_fan: 135,
  T_inv_derate_start: 105, T_inv_derate_end: 120,
  T_motor_derate_start: 150, T_motor_derate_end: 170,
  P_dis_ref: 460000, P_chg_ref: 150000,
  Kp_id: 1.50, Ki_id: 86, Kp_iq: 3.00, Ki_iq: 86,
  Kp_spd: 162, Ki_spd: 3380,
  Id_ref_base: 0, dead_time: 1.5e-6, ctrl_dt: 0.00005, carrier_freq: 12000,
  torque_rate_limit: 9500,
  bms_i_charge_max: 255, bms_i_discharge_max: 700,
  bms_soc_charge_cutoff: 0.97, bms_soc_discharge_cutoff: 0.04,
  bms_t_discharge_min: -25, bms_t_discharge_max: 60,
  bms_cell_count_s: 192, bms_cell_count_p: 2,
  bms_cold_derate_temp: 2, bms_cold_derate_factor: 0.39,
  v_max_chart: 260, v_max_sw: 260,
};

// ── KIA_EV9_AWD_PARAMS ──
const KIA_EV9_AWD_PARAMS = {
  name: "Kia EV9 LR AWD", shortName: "E9A", color: "#065f46",
  motor_config: "dual", motor_front_power_ratio: 0.30, awd_enable: true,
  torque_split_front: 0.35,
  one_pedal_enable: true, regen_one_pedal_level: 0.40,
  // Vehicle — Cd=0.280, A≈2.76, CdA≈0.773 (대형 SUV)
  m: 2597, r_wheel: 0.360, CdA: 0.773, Crr: 0.0110, rho: 1.2, mu: 1.0,
  l_f: 1.52, l_r: 1.53, wheel_base: 3.100, h_cg: 0.68, Iz: 5200,
  // Powertrain — 283 kW / 600 Nm (전 100kW + 후 183kW)
  gear_ratio: 9.0, eta_driveline: 0.96, T_max_motor: 600, P_max_wheel: 283000,
  P_max_battery: 310000, P_max_regen: 150000, battery_energy: 99.8, soc_init: 0.9,
  eta_motor_inv: 0.93, aux_power: 2000,
  Rs: 0.030, Ld: 0.00055, Lq: 0.00110, lambda_pm: 0.132, poles: 8, J_motor: 0.070,
  V_dc: 680, I_max_rms: 460, m_a: 1.0,
  R_batt: 0.038, Q_nom: 142, batt_temp: 25,
  k_h: 0.020, k_e: 0.000090, k_m0: 30, k_m1: 0.013, k_m2: 0.0000062,
  k_g0: 40, k_g1: 0.0028, k_g2: 0.000014,
  C_th_inv: 2800, R_th_ic: 0.060, k_inv_temp: 0.002,
  C_th_motor: 16000, R_th_mc: 0.110, alpha_Cu: 0.0039, k_lambda: 0.001, T_ref: 25,
  C_th_cool: 22000, UA_rad: 290, phi_speed: 0.5, P_pump: 210, P_fan: 150,
  T_inv_derate_start: 100, T_inv_derate_end: 115,
  T_motor_derate_start: 145, T_motor_derate_end: 165,
  P_dis_ref: 310000, P_chg_ref: 150000,
  Kp_id: 1.56, Ki_id: 90, Kp_iq: 3.12, Ki_iq: 90,
  Kp_spd: 160, Ki_spd: 3300,
  Id_ref_base: 0, dead_time: 1.5e-6, ctrl_dt: 0.00005, carrier_freq: 12000,
  torque_rate_limit: 5500,
  bms_i_charge_max: 260, bms_i_discharge_max: 490,
  bms_soc_charge_cutoff: 0.97, bms_soc_discharge_cutoff: 0.04,
  bms_t_discharge_min: -25, bms_t_discharge_max: 60,
  bms_cell_count_s: 192, bms_cell_count_p: 2,
  bms_cold_derate_temp: 3, bms_cold_derate_factor: 0.42,
  v_max_chart: 200, v_max_sw: 200,
};

// ── KIA_EV3_RWD_PARAMS ──
const KIA_EV3_RWD_PARAMS = {
  name: "Kia EV3 LR RWD", shortName: "E3R", color: "#14532d",
  motor_config: "single_rear", motor_front_power_ratio: 0, awd_enable: false,
  one_pedal_enable: true, regen_one_pedal_level: 0.40,
  // Vehicle — Cd=0.260, A≈2.15, CdA≈0.559 (소형 SUV)
  m: 1685, r_wheel: 0.328, CdA: 0.559, Crr: 0.0092, rho: 1.2, mu: 1.0,
  l_f: 1.37, l_r: 1.38, wheel_base: 2.760, h_cg: 0.52, Iz: 2200,
  // Powertrain — 150 kW / 310 Nm 단일 후륜
  gear_ratio: 9.8, eta_driveline: 0.96, T_max_motor: 310, P_max_wheel: 150000,
  P_max_battery: 175000, P_max_regen: 100000, battery_energy: 81.4, soc_init: 0.9,
  eta_motor_inv: 0.94, aux_power: 1200,
  Rs: 0.022, Ld: 0.00042, Lq: 0.00084, lambda_pm: 0.118, poles: 8, J_motor: 0.045,
  V_dc: 400, I_max_rms: 380, m_a: 1.0,
  R_batt: 0.065, Q_nom: 200, batt_temp: 25,
  k_h: 0.019, k_e: 0.000088, k_m0: 18, k_m1: 0.009, k_m2: 0.0000042,
  k_g0: 28, k_g1: 0.0017, k_g2: 0.0000085,
  C_th_inv: 1900, R_th_ic: 0.080, k_inv_temp: 0.003,
  C_th_motor: 10500, R_th_mc: 0.150, alpha_Cu: 0.0039, k_lambda: 0.001, T_ref: 25,
  C_th_cool: 13000, UA_rad: 175, phi_speed: 0.5, P_pump: 125, P_fan: 85,
  T_inv_derate_start: 95, T_inv_derate_end: 110,
  T_motor_derate_start: 140, T_motor_derate_end: 160,
  P_dis_ref: 175000, P_chg_ref: 100000,
  Kp_id: 1.48, Ki_id: 82, Kp_iq: 2.96, Ki_iq: 82,
  Kp_spd: 128, Ki_spd: 2650,
  Id_ref_base: 0, dead_time: 2.0e-6, ctrl_dt: 0.0001, carrier_freq: 10000,
  torque_rate_limit: 4000,
  bms_i_charge_max: 190, bms_i_discharge_max: 450,
  bms_soc_charge_cutoff: 0.97, bms_soc_discharge_cutoff: 0.05,
  bms_t_discharge_min: -20, bms_t_discharge_max: 60,
  bms_cell_count_s: 108, bms_cell_count_p: 4,
  bms_cold_derate_temp: 5, bms_cold_derate_factor: 0.48,
  v_max_chart: 170, v_max_sw: 170,
};

// ── KIA_EV6_RWD_PARAMS ──
const KIA_EV6_RWD_PARAMS = {
  name: "Kia EV6 LR RWD", shortName: "E6R", color: "#16a34a",
  motor_config: "single_rear", motor_front_power_ratio: 0, awd_enable: false,
  one_pedal_enable: true, regen_one_pedal_level: 0.45,
  m: 1870, r_wheel: 0.338, CdA: 0.513, Crr: 0.0100, rho: 1.2, mu: 1.0,
  l_f: 1.41, l_r: 1.46, wheel_base: 2.900, h_cg: 0.53, Iz: 2900,
  // Powertrain — 168 kW / 350 Nm RWD
  gear_ratio: 9.7, eta_driveline: 0.96, T_max_motor: 350, P_max_wheel: 168000,
  P_max_battery: 195000, P_max_regen: 150000, battery_energy: 77.4, soc_init: 0.9,
  eta_motor_inv: 0.94, aux_power: 1450,
  Rs: 0.030, Ld: 0.00054, Lq: 0.00108, lambda_pm: 0.128, poles: 8, J_motor: 0.052,
  V_dc: 680, I_max_rms: 300, m_a: 1.0,
  R_batt: 0.050, Q_nom: 110, batt_temp: 25,
  k_h: 0.018, k_e: 0.000080, k_m0: 21, k_m1: 0.010, k_m2: 0.0000048,
  k_g0: 32, k_g1: 0.0020, k_g2: 0.000010,
  C_th_inv: 2200, R_th_ic: 0.070, k_inv_temp: 0.002,
  C_th_motor: 12500, R_th_mc: 0.130, alpha_Cu: 0.0039, k_lambda: 0.001, T_ref: 25,
  C_th_cool: 16000, UA_rad: 225, phi_speed: 0.5, P_pump: 158, P_fan: 102,
  T_inv_derate_start: 100, T_inv_derate_end: 115,
  T_motor_derate_start: 145, T_motor_derate_end: 165,
  P_dis_ref: 195000, P_chg_ref: 150000,
  Kp_id: 1.53, Ki_id: 87, Kp_iq: 3.06, Ki_iq: 87,
  Kp_spd: 142, Ki_spd: 2950,
  Id_ref_base: 0, dead_time: 1.5e-6, ctrl_dt: 0.00005, carrier_freq: 12000,
  torque_rate_limit: 5000,
  bms_i_charge_max: 220, bms_i_discharge_max: 350,
  bms_soc_charge_cutoff: 0.97, bms_soc_discharge_cutoff: 0.04,
  bms_t_discharge_min: -25, bms_t_discharge_max: 60,
  bms_cell_count_s: 192, bms_cell_count_p: 2,
  bms_cold_derate_temp: 3, bms_cold_derate_factor: 0.43,
  v_max_chart: 185, v_max_sw: 185,
};

// ── [Codegen] Preset Gain Version ──────────────────────────
// codegen 스크립트(scripts/gen_presets.js)가 Kp/Ki를 재계산할 때
// 이 날짜 문자열을 갱신하면 paramsStore가 localStorage의 구형
// gain 캐시를 자동으로 flush하고 새 값으로 교체합니다.
// 형식: 'YYYY-MM-DD' (codegen 실행일 기준으로 갱신)
const PRESET_GAIN_VERSION = '2026-04-05';

// ── VEHICLE_PRESETS ──
const VEHICLE_PRESETS = {
  // ─── Tesla ────────────────────────────────────────
  "tesla_m3":          { ...DEFAULT_PARAMS, ...TESLA_M3_PARAMS,          drive_mode: "NORMAL", cycle_type: "WLTP" },
  "tesla_m3_rwd":      { ...DEFAULT_PARAMS, ...TESLA_M3_RWD_PARAMS,      drive_mode: "NORMAL", cycle_type: "WLTP" },
  "tesla_my":          { ...DEFAULT_PARAMS, ...TESLA_MY_PARAMS,           drive_mode: "NORMAL", cycle_type: "WLTP" },
  "tesla_ms_plaid":    { ...DEFAULT_PARAMS, ...TESLA_MS_PLAID_PARAMS,     drive_mode: "NORMAL", cycle_type: "WLTP" },
  "tesla_mx":          { ...DEFAULT_PARAMS, ...TESLA_MX_PARAMS,           drive_mode: "NORMAL", cycle_type: "WLTP" },
  // ─── Hyundai ──────────────────────────────────────
  "ioniq_6":           { ...DEFAULT_PARAMS, ...IONIQ6_PARAMS,             drive_mode: "NORMAL", cycle_type: "WLTP" },
  "ioniq_6_rwd":       { ...DEFAULT_PARAMS, ...IONIQ6_RWD_PARAMS,         drive_mode: "NORMAL", cycle_type: "WLTP" },
  "ioniq_5_awd":       { ...DEFAULT_PARAMS, ...IONIQ5_AWD_PARAMS,         drive_mode: "NORMAL", cycle_type: "WLTP" },
  "ioniq_5_rwd":       { ...DEFAULT_PARAMS, ...IONIQ5_RWD_PARAMS,         drive_mode: "NORMAL", cycle_type: "WLTP" },
  "ioniq_5_n":         { ...DEFAULT_PARAMS, ...IONIQ5_N_PARAMS,           drive_mode: "SPORT",  cycle_type: "WLTP" },
  // ─── Kia ──────────────────────────────────────────
  "kia_ev6_awd":       { ...DEFAULT_PARAMS, ...KIA_EV6_AWD_PARAMS,        drive_mode: "NORMAL", cycle_type: "WLTP" },
  "kia_ev6_rwd":       { ...DEFAULT_PARAMS, ...KIA_EV6_RWD_PARAMS,        drive_mode: "NORMAL", cycle_type: "WLTP" },
  "kia_ev6_gt":        { ...DEFAULT_PARAMS, ...KIA_EV6_GT_PARAMS,         drive_mode: "SPORT",  cycle_type: "WLTP" },
  "kia_ev9_awd":       { ...DEFAULT_PARAMS, ...KIA_EV9_AWD_PARAMS,        drive_mode: "NORMAL", cycle_type: "WLTP" },
  "kia_ev3_rwd":       { ...DEFAULT_PARAMS, ...KIA_EV3_RWD_PARAMS,        drive_mode: "NORMAL", cycle_type: "WLTP" },
};

// ── exports ──
// ── 버전 검증 유틸리티 ──────────────────────────────────────────────────────
// checkPresetGainVersion(cachedVersion) → { ok, current, cached, message }
// paramsStore / zustand 등에서 localStorage 캐시 flush 여부 판단에 사용.
//
// 사용 예:
//   import { checkPresetGainVersion, PRESET_GAIN_VERSION } from './VEHICLE_PRESETS';
//   const { ok, message } = checkPresetGainVersion(localStorage.getItem('gainVersion'));
//   if (!ok) { clearGainCache(); localStorage.setItem('gainVersion', PRESET_GAIN_VERSION); }
export function checkPresetGainVersion(cachedVersion) {
  const ok = cachedVersion === PRESET_GAIN_VERSION;
  return {
    ok,
    current: PRESET_GAIN_VERSION,
    cached:  cachedVersion ?? null,
    message: ok
      ? `[VEHICLE_PRESETS] gain version OK (${PRESET_GAIN_VERSION})`
      : `[VEHICLE_PRESETS] ⚠ gain version mismatch — cached: "${cachedVersion}", current: "${PRESET_GAIN_VERSION}". Cache will be flushed.`,
  };
}

export {
  PRESET_GAIN_VERSION,
  DEFAULT_PARAMS,
  TESLA_M3_PARAMS,
  TESLA_M3_RWD_PARAMS,
  IONIQ6_PARAMS,
  IONIQ6_RWD_PARAMS,
  TESLA_MY_PARAMS,
  TESLA_MS_PLAID_PARAMS,
  TESLA_MX_PARAMS,
  IONIQ5_AWD_PARAMS,
  IONIQ5_RWD_PARAMS,
  IONIQ5_N_PARAMS,
  KIA_EV6_AWD_PARAMS,
  KIA_EV6_GT_PARAMS,
  KIA_EV9_AWD_PARAMS,
  KIA_EV3_RWD_PARAMS,
  KIA_EV6_RWD_PARAMS,
  VEHICLE_PRESETS,
};